import { fetchSyncReadTransport } from './readTransport.js';
import { resolveSupersededStockClosures } from './supersededStockClosures.js';
import { prepareStockCommandPush } from './prepareStockCommandPush.js';
import { readQueueAttentionCount } from './queueHealth.js';
import {
  SYNC_BATCH_SIZE,
  SYNC_INTERVAL_MS,
  SYNC_PUSH_BATCH_SIZE,
  ShipmentStockVoidPreparationV1Schema,
  ShipmentStockVoidResponseV1Schema,
  ShipmentWithoutStockIssueResponseV1Schema,
  ShipmentWithoutStockVoidResponseV1Schema,
  MasterOpSchema,
  isOfflineRegisterTable,
  StockCommandMasterEffectV1Schema,
  StockCommandEnvelopeV1Schema,
  StockCommandV1Schema,
  SyncPullResponseSchema,
  SyncEventInputSchema,
  SyncPushResponseSchema,
  materializeStockCommand,
  type MasterOp,
  type ShipmentStockVoidPreparationV1,
  type ShipmentStockVoidResponseV1,
  type ShipmentWithoutStockIssueResponseV1,
  type ShipmentWithoutStockVoidResponseV1,
  type StockCommandV1,
  type StockCommandEnvelopeV1,
  type StockCommandMasterEffectV1,
  type SyncEventInput,
  type SyncPullResponse,
  type SyncPushRequest,
  type SyncPushResponse,
} from '@trackeat/shared';
import { getSupabase, SUPABASE_URL } from '../supabase.js';
import { isCurrentPersistedSession, readPersistedSession } from '../auth/sessionStorage.js';
import type { Session } from '@supabase/supabase-js';
import {
  attachActiveOperatorAssertion,
  getActiveSharedTabletOperator,
  decorateSyncEventForOperator,
  optimisticOperatorPayload,
  requireActiveOperatorAssertion,
  splitOperatorAssertion,
  stockCommandForActiveOperator,
} from '../auth/activeOperatorSession.js';
import { activateSharedTabletOperatorGrant } from '../auth/sharedTabletOperatorApi.js';
import { toUserMessage } from '../errors.js';
import {
  deletePendingOp,
  enqueuePendingOp,
  findPendingMasterOp,
  flushLocalDb,
  getMeta,
  listPendingOps,
  listRunnablePendingOps,
  markPendingOpFailed,
  openLocalDb,
  pendingOpIsolationKeyV1,
  pendingRegisterMatchesScope,
  quarantinePendingOp,
  requeueCleaningProductReferenceRejectionsOnce,
  requeueSharedTabletHaccpRejectionsOnce,
  requeueLegacyPrinterRejectionsOnce,
  setMeta,
  type PendingOp,
  type PendingOpRow,
  type PendingRegisterScope,
} from '../db/local.js';
import { dbg } from '../debug/debugLog.js';
import {
  confirmedEventLocalIdentity,
  eventsLocalRowIdDetailed,
} from './eventsLocalKey.js';
import {
  drainPullPages,
  initialPullCursor,
} from './pullPagination.js';
import {
  finalizeLegacyEventServerIdRecovery,
  recoverLegacyEventServerIds,
  resetPullCursors,
} from './pullCursorRecovery.js';
import { purgeProductionLotPhotoOutbox } from '../labels/productionLotPhotoOutbox.js';
import {
  drainOperationalEvidenceOutbox,
  purgeOperationalEvidenceOutsideTenants,
} from '../haccp/operationalEvidenceOutbox.js';
import { SingleFlightRerun } from './singleFlightRerun.js';
import {
  beginStockCommandSend,
  enqueueStockCommand,
  rejectStockCommandPermanently,
  retryStockCommand,
  stockCommandDigest,
  type EnqueuedStockCommandHandle,
} from './stockCommandOutbox.js';
import {
  acknowledgeStockCommandWriteback,
  applyPulledMasterToStockCommandCache,
  changedPulledMasters,
  applyStockCommandWritebackLocally,
  countPendingStockCommands,
  countStockCommandReconciliations,
  countUnarchivedPermanentStockCommandRejections,
  enqueueLegacyMasterOp,
  getPendingStockCommandBatch,
  listPendingStockCommandBatches,
  stockCommandSqliteStorage,
  type RegisterLocalCommit,
} from './stockCommandSqlite.js';
import {
  validateProductionLabelOutcomeWritebackV1,
  validateShipmentWithoutStockIssueWritebackV1,
  validateShipmentWithoutStockVoidWritebackV1,
  validateStockCommandAckWritebackV1,
} from './stockCommandAckValidator.js';
import {
  acknowledgeProductionLabelPrintOutcome,
  adoptConfirmedProductionLabelPrintOutcome,
  adoptProductionLabelPrintIntents,
  beginProductionLabelPrintOutcomeSend,
  countPendingProductionLabelPrintOutcomes,
  finalizeProductionLabelPrintIntentLocally,
  isProductionLabelCommandReadyForPush,
  listPendingProductionLabelPrintOutcomes,
  listProductionLabelPrintOutcomesForScope,
  listProductionLabelPrintReconciliations,
  listProductionLabelPrintIntentsLocal,
  reconcileRejectedProductionLabelPrintOutcomes,
  resolveProductionLabelPrintReconciliation as resolveProductionLabelPrintReconciliationLocally,
  retryProductionLabelPrintOutcome,
  type FinalizeProductionLabelPrintIntentInput,
  type PendingProductionLabelPrintOutcome,
  type ProductionLabelPrintReconciliation,
  type ProductionLabelPrintIntent,
  type ProductionLabelPrintIntentState,
} from './productionLabelPrintIntentOutbox.js';
import {
  assertFreshEventIsNotDirectStock,
  boundPendingPushBytes,
  buildLegacyPendingPushRequest,
  isLegacyDirectStockEvent,
} from './legacyPendingPush.js';
import {
  isTerminalLegacyStockRejection,
  quarantineLegacyStockPendingOp,
} from './legacyStockReconciliation.js';
import {
  masterRowContainsExpectedPatch,
  matchingMasterConflictConfirmsExpectedPatch,
} from './masterConfirmation.js';
import { shouldQuarantineRejectedSyncOp } from './masterRejectionRetry.js';
import { emitSyncHealth } from './syncHealth.js';
import { mustYieldForLegacyAfterReopenAckV1 } from './productionDayCausalOrder.js';
import {
  acknowledgeAtomicRpcV1,
  beginAtomicRpcSendV1,
  countPendingAtomicRpcsV1,
  enqueueAtomicRpcV1,
  hasProductionPlanConflictForDayV1,
  latestAtomicRpcConflictV1,
  listPendingAtomicRpcsV1,
  markAtomicRpcConflictCascadeV1,
  resolveAtomicRpcConflictAfterPullV1,
  retryAtomicRpcV1,
  type AtomicRpcHandle,
  type AtomicRpcKind,
  type AtomicRpcRecord,
} from './atomicRpcOutbox.js';
import {
  isRetryableAtomicRpcError,
  isSyncTransportUnavailableError,
} from './atomicRpcRetry.js';
import {
  acknowledgeProductionPlanReplaceResultV1,
  productionPlanScopeKey,
  queueProductionPlanReplacementV1,
  type ProductionPlanReplaceRequestV1,
  type QueueProductionPlanReplacementInput,
} from './productionPlanOutbox.js';
import {
  buildProductionDayTransitionRequestV1,
  parseProductionDayTransitionResultV1,
  prepareProductionDayTransitionV1,
  type BuildProductionDayTransitionInputV1,
  type ProductionDayTransitionRequestV1,
  type ProductionDayTransitionRowV1,
} from '../productionDayTransition.js';
import {
  parseStockReconciliationResultV1,
  queueStockReconciliationV1,
  type BuildStockReconciliationInputV1,
  type StockReconciliationRequestV1,
} from '../warehouse/stockReconciliation.js';
import {
  parseConsultantHistoricalLotRecountResultV1,
  queueConsultantHistoricalLotRecountV1,
  type BuildConsultantHistoricalLotRecountInputV1,
  type ConsultantHistoricalLotRecountRequestV1,
} from '../warehouse/historicalLotRecount.js';
import {
  parseFoodSupplierMatchArchiveResultV1,
  queueFoodSupplierMatchArchiveV1,
  type FoodSupplierMatchArchiveRequestV1,
  type QueueFoodSupplierMatchArchiveInputV1,
} from '../warehouse/foodSupplierMatchArchive.js';
import {
  parseSupplierItemMatchManagementResultV1,
  queueSupplierItemMatchManagementV1,
  type QueueSupplierItemMatchManagementInputV1,
  type SupplierItemMatchManagementRequestV1,
} from '../suppliers/supplierItemMatchManagement.js';

const SYNC_FETCH_TIMEOUT_MS = 20_000;
const DRIFT_STALE_THRESHOLD_MS = 5 * 60 * 1000;
// Upper bound on how many push chunks one tick will drain before yielding.
// Only chunks that drain CLEANLY (every op accepted/conflict-resolved) chain
// into the next, so a large idempotent backlog clears in a single tick while a
// chunk that leaves retryable failures stops the loop for the next tick.
const MAX_PUSH_CHUNKS_PER_TICK = 12;

function activeBusinessFromJwt(accessToken: string): string | null {
  try {
    const raw = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(raw.padEnd(raw.length + (4 - raw.length % 4) % 4, '='))) as { active_business_id?: unknown };
    return typeof claims.active_business_id === 'string' ? claims.active_business_id : null;
  } catch { return null; }
}

function currentRegisterScope() {
  const session = readPersistedSession(SUPABASE_URL);
  return session ? {
    accountId: session.user.id,
    tenantId: activeTenantFromJwt(session.access_token),
    businessId: activeBusinessFromJwt(session.access_token),
    operator: getActiveSharedTabletOperator(),
  } : undefined;
}

function assertCurrentRegisterScope(expected: PendingRegisterScope): void {
  const current = currentRegisterScope();
  if (!current || current.accountId !== expected.accountId || current.tenantId !== expected.tenantId
    || current.businessId !== expected.businessId || current.operator !== (expected.operator ?? null)
    || (current.operator && Date.parse(current.operator.grantExpiresAt) <= Date.now())) {
    throw new Error('register_sync_scope_changed');
  }
}

function activeTenantFromJwt(accessToken: string): string | null {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - normalized.length % 4) % 4),
      '=',
    );
    const claims = JSON.parse(atob(padded)) as {
      tenant_id?: unknown;
      active_tenant_id?: unknown;
    };
    const tenant = claims.tenant_id ?? claims.active_tenant_id;
    return typeof tenant === 'string' && tenant.trim() ? tenant : null;
  } catch {
    return null;
  }
}

async function activateOperatorAssertionForDevice(
  assertion: ReturnType<typeof requireActiveOperatorAssertion>,
  deviceId: string,
): Promise<void> {
  if (!assertion) return;
  await activateSharedTabletOperatorGrant({
    grantId: assertion.grant_id,
    grantToken: assertion.grant_token,
    tenantPersonnelId: assertion.tenant_personnel_id,
    deviceId,
  });
}

async function prepareStockEnvelopeForServer(
  envelope: StockCommandEnvelopeV1,
): Promise<StockCommandEnvelopeV1> {
  const first = envelope.events[0]
    ? splitOperatorAssertion(envelope.events[0])
    : null;
  const assertion = envelope.operator_assertion ?? first?.assertion;
  await activateOperatorAssertionForDevice(assertion, envelope.device_id);
  const withoutRootAssertion = { ...envelope };
  delete withoutRootAssertion.operator_assertion;
  return {
    ...withoutRootAssertion,
    events: envelope.events.map((event) => splitOperatorAssertion(event).value),
  };
}

const MASTER_ONLY_OPERATOR_META_PREFIX = 'shared_tablet.stock_master_operator_v1:';

function masterOnlyOperatorMetaKey(commandId: string): string {
  return `${MASTER_ONLY_OPERATOR_META_PREFIX}${commandId}`;
}

async function masterOnlyEnvelopeOperator(input: {
  commandId: string;
  fallbackDeviceId: string;
}): Promise<{
  deviceId: string;
  assertion: ReturnType<typeof requireActiveOperatorAssertion>;
}> {
  const assertion = requireActiveOperatorAssertion();
  const raw = await getMeta(masterOnlyOperatorMetaKey(input.commandId));
  if (!raw) return { deviceId: input.fallbackDeviceId, assertion };
  let expected: { tenant_personnel_id?: unknown; device_id?: unknown };
  try {
    expected = JSON.parse(raw) as typeof expected;
  } catch {
    throw new Error('shared_tablet_master_operator_cache_invalid');
  }
  if (
    !assertion
    || assertion.tenant_personnel_id !== expected.tenant_personnel_id
  ) {
    throw new Error('shared_tablet_original_operator_required');
  }
  return {
    deviceId: typeof expected.device_id === 'string'
      ? expected.device_id
      : input.fallbackDeviceId,
    assertion,
  };
}

function atomicRpcFailureReason(error: unknown): string {
  if (error && typeof error === 'object') {
    const value = error as { code?: unknown; message?: unknown };
    const code = typeof value.code === 'string' ? value.code : '';
    const message = typeof value.message === 'string'
      ? value.message
      : toUserMessage(error);
    return code ? `${code}:${message}` : message;
  }
  return toUserMessage(error);
}

function isProductionLabelOutcomeReplayConflict(error: unknown): boolean {
  const value = error && typeof error === 'object'
    ? error as { code?: unknown; message?: unknown }
    : {};
  const code = typeof value.code === 'string' ? value.code : '';
  const message = typeof value.message === 'string'
    ? value.message
    : toUserMessage(error);
  return code === 'label_print_intent_replay_conflict'
    || /label_print_intent_replay_conflict/i.test(message);
}

export type StockCommandPushConfirmation =
  | { confirmed: true }
  | {
      confirmed: false;
      status: 'pending' | 'permanently_rejected';
      reason: string;
    };

export interface SyncEngineOptions {
  intervalMs?: number;
  batchSize?: number;
  pushBatchSize?: number;
}

export type SyncStatus = 'idle' | 'pulling' | 'pushing' | 'error' | 'offline';

export interface SyncState {
  status: SyncStatus;
  lastError: string | null;
  lastPullAt: string | null;
  /** A successful network check can contain no local data changes. */
  lastPullChangedData?: boolean;
  lastPushAt: string | null;
  pendingCount: number;
  /** Rejected commands preserved outside the runnable queue for repair. */
  reconciliationCount?: number;
  queueAttentionCount?: number;
  queueTenantId?: string;
}

type Listener = (state: SyncState) => void;

class SyncEngine {
  private timer: ReturnType<typeof setInterval> | null = null;
  private pausedForUpdate = false;
  private readonly tickRerun = new SingleFlightRerun();
  private readonly listeners = new Set<Listener>();
  private consecutiveFailures = 0;
  private offlineScope: { accountId: string; tenantId: string | null } | null = null;
  private state: SyncState = {
    status: 'idle',
    lastError: null,
    lastPullAt: null,
    lastPushAt: null,
    pendingCount: 0,
    reconciliationCount: 0,
  };
  private readonly intervalMs: number;
  private readonly batchSize: number;
  private readonly pushBatchSize: number;

  constructor(opts: SyncEngineOptions = {}) {
    this.intervalMs = opts.intervalMs ?? SYNC_INTERVAL_MS;
    this.batchSize = opts.batchSize ?? SYNC_BATCH_SIZE;
    this.pushBatchSize = opts.pushBatchSize ?? SYNC_PUSH_BATCH_SIZE;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  getState(): SyncState {
    return this.state;
  }

  /** Scope only: offline queue reads must not trigger an expired-token refresh. */
  setOfflineSession(session: Pick<Session, 'access_token' | 'user'> | null): void {
    const tenantId = session ? activeTenantFromJwt(session.access_token) : null;
    if (session && this.offlineScope?.accountId === session.user.id
      && this.offlineScope.tenantId === tenantId) return;
    this.offlineScope = session ? { accountId: session.user.id, tenantId } : null;
  }

  private offlineActiveTenantId(): string {
    if (!this.offlineScope) throw new Error('atomic_rpc_not_authenticated');
    if (!this.offlineScope.tenantId) throw new Error('atomic_rpc_active_tenant_missing');
    return this.offlineScope.tenantId;
  }

  private setState(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  private async countLegacyPending(): Promise<number> {
    const scope = currentRegisterScope();
    return (await listPendingOps(-1)).filter((row) => pendingRegisterMatchesScope(row, scope)).length;
  }

  private async countAllPending(): Promise<number> {
    return (
      await this.countLegacyPending()
      + await countPendingAtomicRpcsV1()
      + await countPendingStockCommands()
      + await countPendingProductionLabelPrintOutcomes()
    );
  }

  async start(): Promise<void> {
    if (this.pausedForUpdate || this.timer) return;
    // Install recovery before any database/network await. A failed initial
    // open must not leave the engine without a timer until the app restarts.
    this.timer = setInterval(() => {
      void this.tick();
    }, this.intervalMs);
    await this.tick();
  }

  async resumeAfterUpdateFailure(): Promise<void> {
    this.pausedForUpdate = false;
    await this.start();
  }

  async pauseForUpdate(): Promise<void> {
    this.pausedForUpdate = true;
    this.stop();
    // Do not close SQLite while a started drain is applying a server response.
    await this.tickRerun.waitForIdle();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async queueEvent(evt: SyncEventInput): Promise<void> {
    // Fresh physical-stock writes must always cross the atomic StockCommand
    // path. Only rows that were already present in the legacy outbox before
    // this client upgrade may drain through `buildLegacyPendingPushRequest`.
    const attributedEvent = decorateSyncEventForOperator(evt);
    assertFreshEventIsNotDirectStock(attributedEvent);
    await enqueuePendingOp({ kind: 'event', payload: attributedEvent });
    // Ottimistico: inserisci subito l'evento in events_local così useEvents lo
    // rende visibile prima del round-trip server. Usiamo client_uuid come PK
    // locale: quando il pull tornerà l'evento confermato, applyServerEvents
    // farà INSERT OR REPLACE sulla stessa riga (vedi `pull()` sotto).
    // rev=NULL distingue le righe pending dalle confermate (per ORDER BY e
    // futuri filtri di diagnostica).
    try {
      const db = await openLocalDb(null);
      const key = eventsLocalRowIdDetailed(attributedEvent);
      await db.run(
        `INSERT OR REPLACE INTO events_local
           (id, server_id, tenant_id, kind, business_id, client_ts, server_ts,
            rev, payload, self_hash, prev_hash)
         VALUES (?, NULL, ?, ?, ?, ?, NULL, NULL, ?, NULL, NULL)`,
        [
          key.id,
          attributedEvent.tenant_id,
          attributedEvent.kind,
          attributedEvent.business_id ?? null,
          attributedEvent.client_ts,
          JSON.stringify(optimisticOperatorPayload(attributedEvent.payload ?? {})),
        ],
      );
      // T-312/#4: traccia la PK scritta. `server_id` come chiave = il fallback
      // che causò T-311 → warn (sempre flushato); il caso normale = info.
      dbg[key.source === 'server_id' ? 'warn' : 'info'](
        'events_local',
        key.source === 'server_id' ? 'write_keyed_on_server_id' : 'write',
        { writer: 'queue', id: key.id, kind: attributedEvent.kind, key_source: key.source, rev: null },
      );
    } catch (err) {
      // Non fatale: il sync recupererà l'evento via push/pull più tardi.
      dbg.warn('mobile-sync', 'optimistic_event_write_failed', {
        client_uuid: attributedEvent.client_uuid,
        kind: attributedEvent.kind,
        error: toUserMessage(err),
      });
    }
  }

  async queueStockCommand(
    commandInput: StockCommandV1,
    masterEffectsInput: readonly StockCommandMasterEffectV1[] = [],
  ): Promise<EnqueuedStockCommandHandle> {
    const command = StockCommandV1Schema.parse(stockCommandForActiveOperator(commandInput));
    const masterEffects = masterEffectsInput.map((effect) => (
      StockCommandMasterEffectV1Schema.parse(effect)
    ));
    const deviceId = (await getMeta('device_id')) ?? 'unknown';
    const clientTs = new Date().toISOString();
    const optimisticEvents: SyncEventInput[] = materializeStockCommand(command)
      .map((draft) => decorateSyncEventForOperator({
        client_uuid: crypto.randomUUID(),
        tenant_id: draft.tenant_id,
        business_id: draft.business_id,
        device_id: deviceId,
        client_ts: clientTs,
        kind: draft.kind,
        payload: draft.payload,
      }));
    const masterOnlyAssertion = optimisticEvents.length === 0 && masterEffects.length > 0
      ? requireActiveOperatorAssertion()
      : undefined;
    // Validate the complete cross-effect contract before acknowledging a local
    // save (and therefore before the label flow can print). Push uses the same
    // schema again for envelopes persisted by older application versions.
    StockCommandEnvelopeV1Schema.parse({
      schema: 'trackeat.stock-command', version: 1, command,
      command_digest: await stockCommandDigest(command, masterEffects),
      device_id: deviceId, operator_assertion: masterOnlyAssertion,
      events: optimisticEvents, master_effects: masterEffects,
    });
    const queued = await enqueueStockCommand(
      stockCommandSqliteStorage,
      command,
      optimisticEvents,
      { masterEffects },
    );
    if (queued.outcome === 'already_rejected') {
      throw new Error('stock_command_permanently_rejected');
    }
    if (masterOnlyAssertion) {
      await setMeta(masterOnlyOperatorMetaKey(command.command_id), JSON.stringify({
        tenant_personnel_id: masterOnlyAssertion.tenant_personnel_id,
        device_id: deviceId,
      }));
    }
    // On web, jeep-sqlite keeps the database in memory until saveToStore().
    // Persist the atomic command + optimistic facts before reporting success to
    // the UI, otherwise a tab/process crash can lose an allegedly queued move.
    await flushLocalDb();
    this.setState({ pendingCount: await countPendingStockCommands() });
    return queued.handle;
  }

  async queueProductionPlanReplacement(
    input: QueueProductionPlanReplacementInput,
  ): Promise<AtomicRpcHandle> {
    const handle = await queueProductionPlanReplacementV1(input);
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.tick();
    }
    return handle;
  }

  async queueStockReconciliation(
    input: Omit<BuildStockReconciliationInputV1, 'deviceId'>,
  ): Promise<AtomicRpcHandle> {
    // An explicit resubmission replaces only this source's rejected request.
    // Pull first; preserve its complete intent in the resolution journal.
    // The new request still goes through server CAS, balance and value checks.
    const conflict = await latestAtomicRpcConflictV1({
      tenantId: input.tenantId,
      kind: 'stock_reconciliation_v1',
      scopeKey: `stock-reconciliation:${input.businessId}:${input.sourceFoodId}`,
    });
    if (conflict) {
      await resolveAtomicRpcConflictAfterPullV1({
        handle: {
          request_id: conflict.request_id,
          kind: conflict.kind,
          tenant_id: conflict.tenant_id,
          scope_key: conflict.scope_key,
          digest: conflict.digest,
        },
        online: typeof navigator === 'undefined' || navigator.onLine,
        pullAuthoritative: () => this.pull(),
        reason: JSON.stringify({
          reason: 'stock_reconciliation_resubmitted_by_user',
          request: conflict.request,
          conflict: conflict.conflict,
        }),
      });
    }
    const handle = await queueStockReconciliationV1(input);
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.tick();
    }
    return handle;
  }

  async queueFoodSupplierMatchArchive(
    input: QueueFoodSupplierMatchArchiveInputV1,
  ): Promise<AtomicRpcHandle> {
    const handle = await queueFoodSupplierMatchArchiveV1(input);
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.tick();
    }
    return handle;
  }

  async queueSupplierItemMatchManagement(
    input: QueueSupplierItemMatchManagementInputV1,
  ): Promise<AtomicRpcHandle> {
    const handle = await queueSupplierItemMatchManagementV1(input);
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.tick();
    }
    return handle;
  }

  async queueConsultantHistoricalLotRecount(
    input: Omit<BuildConsultantHistoricalLotRecountInputV1, 'deviceId'>,
  ): Promise<AtomicRpcHandle> {
    const handle = await queueConsultantHistoricalLotRecountV1(input);
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.tick();
    }
    return handle;
  }

  async latestProductionPlanConflict(input: {
    tenantId: string;
    businessId: string;
    serviceDate: string;
    mealService: QueueProductionPlanReplacementInput['mealService'];
  }): Promise<AtomicRpcRecord | null> {
    return latestAtomicRpcConflictV1({
      tenantId: input.tenantId,
      kind: 'production_plan_replace_v1',
      scopeKey: productionPlanScopeKey(input),
    });
  }

  async resolveProductionPlanConflict(input: {
    tenantId: string;
    businessId: string;
    serviceDate: string;
    mealService: QueueProductionPlanReplacementInput['mealService'];
  }): Promise<void> {
    const conflict = await this.latestProductionPlanConflict(input);
    if (!conflict) throw new Error('production_plan_conflict_not_found');
    await resolveAtomicRpcConflictAfterPullV1({
      handle: {
        request_id: conflict.request_id,
        kind: conflict.kind,
        tenant_id: conflict.tenant_id,
        scope_key: conflict.scope_key,
        digest: conflict.digest,
      },
      online: typeof navigator === 'undefined' || navigator.onLine,
      pullAuthoritative: () => this.pull(),
      reason: 'server_truth_adopted_by_user',
    });
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
  }

  async latestProductionDayConflict(input: {
    tenantId: string;
    businessId: string;
    sheetDate: string;
  }): Promise<AtomicRpcRecord | null> {
    return latestAtomicRpcConflictV1({
      tenantId: input.tenantId,
      kind: 'production_day_transition_v1',
      scopeKey: `${input.businessId}:${input.sheetDate}`,
    });
  }

  async resolveProductionDayConflict(input: {
    tenantId: string;
    businessId: string;
    sheetDate: string;
  }): Promise<void> {
    const conflict = await this.latestProductionDayConflict(input);
    if (!conflict) throw new Error('production_day_conflict_not_found');
    await resolveAtomicRpcConflictAfterPullV1({
      handle: {
        request_id: conflict.request_id,
        kind: conflict.kind,
        tenant_id: conflict.tenant_id,
        scope_key: conflict.scope_key,
        digest: conflict.digest,
      },
      online: typeof navigator === 'undefined' || navigator.onLine,
      pullAuthoritative: () => this.pull(),
      reason: 'server_truth_adopted_by_user',
    });
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
  }

  async queueProductionDayTransition(
    input: BuildProductionDayTransitionInputV1 & { actorUserId: string },
  ): Promise<AtomicRpcHandle> {
    if (await hasProductionPlanConflictForDayV1({
      tenantId: input.tenantId,
      businessId: input.businessId,
      serviceDate: input.sheetDate,
    })) {
      throw new Error('production_day_plan_conflict_requires_resolution');
    }
    const request = buildProductionDayTransitionRequestV1(input);
    const scopeKey = `${input.businessId}:${input.sheetDate}`;
    const queued = await enqueueAtomicRpcV1({
      kind: 'production_day_transition_v1',
      tenantId: input.tenantId,
      businessId: input.businessId,
      scopeKey,
      request,
      optimisticMasters: async (context) => {
        const current = (await context.listMasters('production_days'))
          .find((master) => master.id === input.productionDayId);
        return prepareProductionDayTransitionV1({
          ...input,
          currentRow: current
            ? current.row as ProductionDayTransitionRowV1
            : null,
        });
      },
      absorbLegacyMasterPredecessors:
        input.transition === 'close'
          ? [{
              table: 'production_days',
              id: input.productionDayId,
            }]
          : undefined,
    });
    await flushLocalDb();
    this.setState({ pendingCount: await this.countAllPending() });
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.tick();
    }
    return queued.handle;
  }

  async confirmStockCommandPushed(
    input: EnqueuedStockCommandHandle | string,
  ): Promise<StockCommandPushConfirmation> {
    const commandId = typeof input === 'string' ? input : input.command_id;
    const expectedDigest = typeof input === 'string' ? null : input.digest;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return {
        confirmed: false,
        status: 'pending',
        reason: 'offline',
      };
    }

    const localState = await stockCommandSqliteStorage.transaction(async (tx) => ({
      command: await tx.getCommand(commandId),
      finalization: await tx.getFinalization(commandId),
    }));
    if (
      expectedDigest
      && localState.command
      && localState.command.digest !== expectedDigest
    ) {
      return {
        confirmed: false,
        status: 'permanently_rejected',
        reason: 'local_command_digest_mismatch',
      };
    }
    if (localState.finalization) {
      if (
        expectedDigest
        && localState.finalization.digest !== expectedDigest
      ) {
        return {
          confirmed: false,
          status: 'permanently_rejected',
          reason: 'local_finalization_digest_mismatch',
        };
      }
      return localState.finalization.outcome === 'acknowledged'
        ? { confirmed: true }
        : {
            confirmed: false,
            status: 'permanently_rejected',
            reason: localState.finalization.reason ?? 'permanently_rejected',
          };
    }

    const batch = await getPendingStockCommandBatch(commandId);
    if (!batch) {
      // Recovery after reinstall can know only command_id from the durable
      // server intent. Ask the narrow auth-scoped receipt RPC for proof.
      try {
        const { data, error } = await getSupabase().rpc(
          'confirm_stock_command_receipt_v1',
          { p_command_id: commandId },
        );
        if (error) throw error;
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
          throw new Error('stock_command_confirmation_response_invalid');
        }
        const response = data as Record<string, unknown>;
        if (
          response.schema !== 'trackeat.stock-command-confirmation'
          || response.version !== 1
          || response.command_id !== commandId
          || typeof response.confirmed !== 'boolean'
        ) {
          throw new Error('stock_command_confirmation_response_invalid');
        }
        if (
          response.confirmed
          && (
            expectedDigest === null
            || response.command_digest === expectedDigest
          )
        ) {
          return { confirmed: true };
        }
        if (response.confirmed && expectedDigest !== null) {
          return {
            confirmed: false,
            status: 'permanently_rejected',
            reason: 'server_command_digest_mismatch',
          };
        }
        return {
          confirmed: false,
          status: 'pending',
          reason: 'receipt_not_found',
        };
      } catch (error) {
        return {
          confirmed: false,
          status: 'pending',
          reason: toUserMessage(error, 'Conferma comando non disponibile'),
        };
      }
    }

    const handle = batch.handle;
    if (expectedDigest && handle.digest !== expectedDigest) {
      return {
        confirmed: false,
        status: 'permanently_rejected',
        reason: 'local_command_digest_mismatch',
      };
    }
    try {
      await beginStockCommandSend(stockCommandSqliteStorage, handle);
      const fallbackDeviceId = batch.events[0]?.device_id
        ?? (await getMeta('device_id'))
        ?? 'unknown';
      const masterOnlyOperator = batch.events.length === 0
        ? await masterOnlyEnvelopeOperator({
            commandId: batch.record.command_id,
            fallbackDeviceId,
          })
        : { deviceId: fallbackDeviceId, assertion: undefined };
      const envelope = StockCommandEnvelopeV1Schema.parse({
        schema: 'trackeat.stock-command',
        version: 1,
        command: batch.record.command,
        command_digest: batch.record.digest,
        device_id: masterOnlyOperator.deviceId,
        operator_assertion: masterOnlyOperator.assertion,
        events: batch.events,
        master_effects: [...batch.record.master_effects],
      });
      const body: SyncPushRequest = {
        sync_protocol: 1,
        client_version:
          (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown',
        client_capabilities: [
          'stock_command_v1',
          'stock_command_dead_letter_v1',
          'sync_poison_isolation_v1',
        ],
        events: [],
        master_ops: [],
        stock_commands_v1: [envelope],
      };
      const response = await this.fetchSync(
        'sync-push',
        { method: 'POST', body: JSON.stringify(body) },
        (value) => SyncPushResponseSchema.parse(value),
      );
      const accepted = response.accepted_stock_commands.find(
        (item) => item.command_id === commandId,
      );
      if (accepted) {
        if (accepted.command_digest !== handle.digest) {
          await retryStockCommand(
            stockCommandSqliteStorage,
            handle,
            'server_command_digest_mismatch',
          ).catch(() => undefined);
          return {
            confirmed: false,
            status: 'pending',
            reason: 'server_command_digest_mismatch',
          };
        }
        const validatedResult = validateStockCommandAckWritebackV1({
          envelope,
          result: accepted.result,
        });
        await acknowledgeStockCommandWriteback(handle, validatedResult);
        await flushLocalDb();
        this.setState({ lastPushAt: new Date().toISOString() });
        return { confirmed: true };
      }
      const rejected = response.rejected_stock_commands.find(
        (item) => item.command_id === commandId,
      );
      if (!rejected || rejected.retryable) {
        const reason = rejected?.reason ?? 'stock_command_not_acknowledged';
        await retryStockCommand(
          stockCommandSqliteStorage,
          handle,
          reason,
        ).catch(() => undefined);
        return { confirmed: false, status: 'pending', reason };
      }
      await rejectStockCommandPermanently(
        stockCommandSqliteStorage,
        handle,
        { permanent: true, reason: rejected.reason },
      );
      await reconcileRejectedProductionLabelPrintOutcomes();
      await flushLocalDb();
      return {
        confirmed: false,
        status: 'permanently_rejected',
        reason: rejected.reason,
      };
    } catch (error) {
      const reason = toUserMessage(error, 'Conferma comando non riuscita');
      await retryStockCommand(
        stockCommandSqliteStorage,
        handle,
        reason,
      ).catch(() => undefined);
      return { confirmed: false, status: 'pending', reason };
    }
  }

  async finalizeProductionLabelPrintIntent(
    input: FinalizeProductionLabelPrintIntentInput,
  ): Promise<void> {
    await finalizeProductionLabelPrintIntentLocally({
      ...input,
      operatorAssertion: requireActiveOperatorAssertion(),
    });
    await flushLocalDb();
    this.setState({
      pendingCount:
        await countPendingStockCommands()
        + await countPendingProductionLabelPrintOutcomes()
        + await this.countLegacyPending(),
    });
    // Il workflow fisico termina sulla persistenza locale. La rete resta
    // background/retryable e non puo trasformare una stampa riuscita in errore
    // UI dopo che la carta e gia uscita.
    if (typeof navigator === 'undefined' || navigator.onLine) void this.tick();
  }

  async listProductionLabelPrintIntents(input: {
    states?: readonly ProductionLabelPrintIntentState[];
    localOnly?: boolean;
  } = {}): Promise<ProductionLabelPrintIntent[]> {
    const states = input.states ?? ['pending', 'failed'];
    const local = await listProductionLabelPrintIntentsLocal({ states });
    if (input.localOnly || (typeof navigator !== 'undefined' && !navigator.onLine)) return local;
    try {
      const { data, error } = await getSupabase().rpc(
        'list_production_label_print_intents_v1',
        {
          p_business_id: null,
          // Include terminal server states when local recovery rows remain:
          // a lost response must not require a second physical confirmation.
          p_states: local.length > 0 ? ['pending', 'failed', 'printed', 'voided'] : states,
        },
      );
      if (error) throw error;
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error('production_label_intent_list_response_invalid');
      }
      const response = data as Record<string, unknown>;
      if (
        response.schema !== 'trackeat.production-label-print-intent-list'
        || response.version !== 1
        || !Array.isArray(response.items)
      ) {
        throw new Error('production_label_intent_list_response_invalid');
      }
      const localIds = new Set(local.map((intent) => intent.id));
      await adoptProductionLabelPrintIntents(response.items.filter((item: unknown) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) return true;
        const row = item as Record<string, unknown>;
        return localIds.has(String(row.id)) || states.includes(row.state as ProductionLabelPrintIntentState);
      }));
      await flushLocalDb();
      return listProductionLabelPrintIntentsLocal({ states });
    } catch (error) {
      dbg.warn('mobile-sync', 'production_label_intent_recovery_fetch_failed', {
        error: toUserMessage(error),
        local_count: local.length,
      });
      if (local.length === 0) throw error;
      return local;
    }
  }

  async listProductionLabelPrintSyncState(input: {
    tenantId: string;
    businessId: string;
  }): Promise<{
    pendingSync: PendingProductionLabelPrintOutcome[];
    reconciliationRequired: ProductionLabelPrintReconciliation[];
  }> {
    await reconcileRejectedProductionLabelPrintOutcomes();
    const [pendingSync, reconciliationRequired] = await Promise.all([
      listProductionLabelPrintOutcomesForScope(input),
      listProductionLabelPrintReconciliations(input),
    ]);
    return { pendingSync, reconciliationRequired };
  }

  async resolveProductionLabelPrintReconciliation(input: {
    intentId: string;
    tenantId: string;
    businessId: string;
  }): Promise<void> {
    await resolveProductionLabelPrintReconciliationLocally(input);
    await flushLocalDb();
  }

  /**
   * Online-only status/numbering path for a shipment with no stock legs.
   * The server wrapper owns numbering, shipment_dispatched and the atomic
   * supply-request allocation transition. Its deterministic audit id makes a
   * retry after a lost response exactly-once.
   */
  async issueShipmentWithoutStock(
    shipmentId: string,
  ): Promise<ShipmentWithoutStockIssueResponseV1> {
    const deviceId = (await getMeta('device_id')) ?? 'unknown';
    const tenantId = await this.currentActiveTenantId();
    await activateOperatorAssertionForDevice(
      requireActiveOperatorAssertion(),
      deviceId,
    );
    const { data, error } = await getSupabase().rpc(
      'issue_shipment_without_stock_v1',
      {
        p_shipment_id: shipmentId,
        p_device_id: deviceId,
      },
    );
    if (error) throw error;
    const response = ShipmentWithoutStockIssueResponseV1Schema.parse(data);
    const shipment = response.shipment;
    if (shipment.id !== shipmentId) {
      throw new Error('shipment_issue_without_stock_response_identity_mismatch');
    }

    const validatedResponse =
      validateShipmentWithoutStockIssueWritebackV1({
        tenantId,
        shipmentId,
        deviceId,
        result: response,
      });
    await this.applyStockCommandWriteback(validatedResponse);
    await flushLocalDb();
    this.setState({ lastPushAt: new Date().toISOString() });
    try {
      // Trigger-side allocation updates are ordinary masters. Pull them before
      // the caller invalidates React Query so the order state changes in the
      // same interaction; a pull failure cannot undo the committed RPC.
      await this.pull();
    } catch (pullError) {
      dbg.warn('mobile-sync', 'shipment_issue_supply_pull_failed', {
        shipment_id: shipmentId,
        error: toUserMessage(pullError),
      });
    }
    return response;
  }

  /**
   * Online-only emission path for shipments whose numbering and stock facts
   * must commit together. The server wrapper owns the PostgreSQL transaction;
   * this method never queues the same movement in the command outbox.
   */
  async issueShipmentWithStock(
    shipmentId: string,
    commandInput: StockCommandV1,
  ): Promise<{ doc_number: number; doc_year: number; stock_result: Record<string, unknown> }> {
    const command = StockCommandV1Schema.parse(commandInput);
    if (
      (command.movement_class !== 'transfer' && command.movement_class !== 'dispatch')
      || command.shipment_id !== shipmentId
    ) {
      throw new Error('shipment_stock_command_mismatch');
    }

    const existingFinalization = await stockCommandSqliteStorage.transaction(async (tx) => {
      const active = await tx.getCommand(command.command_id);
      if (active) throw new Error('shipment_stock_command_already_queued');
      return tx.getFinalization(command.command_id);
    });
    const digest = await stockCommandDigest(command);
    if (
      existingFinalization
      && (
        existingFinalization.digest !== digest
        || existingFinalization.outcome !== 'acknowledged'
      )
    ) {
      throw new Error('shipment_stock_command_finalization_conflict');
    }

    const deviceId = (await getMeta('device_id')) ?? 'unknown';
    const clientTs = new Date().toISOString();
    const events: SyncEventInput[] = materializeStockCommand(command).map((draft) => decorateSyncEventForOperator({
      client_uuid: crypto.randomUUID(),
      tenant_id: draft.tenant_id,
      business_id: draft.business_id,
      device_id: deviceId,
      client_ts: clientTs,
      kind: draft.kind,
      payload: draft.payload,
    }));
    const envelope = StockCommandEnvelopeV1Schema.parse({
      schema: 'trackeat.stock-command',
      version: 1,
      command,
      command_digest: digest,
      device_id: deviceId,
      events,
      master_effects: [],
    });

    const serverEnvelope = await prepareStockEnvelopeForServer(envelope);
    const { data, error } = await getSupabase().rpc('issue_shipment_with_stock_v1', {
      p_shipment_id: shipmentId,
      p_stock_command: serverEnvelope,
    });
    if (error) throw error;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('shipment_stock_response_invalid');
    }
    const response = data as Record<string, unknown>;
    const docNumber = Number(response.doc_number);
    const docYear = Number(response.doc_year);
    const stockResult = response.stock_result;
    if (
      !Number.isInteger(docNumber)
      || docNumber <= 0
      || !Number.isInteger(docYear)
      || docYear < 2000
      || !stockResult
      || typeof stockResult !== 'object'
      || Array.isArray(stockResult)
    ) {
      throw new Error('shipment_stock_response_invalid');
    }
    const typedStockResult = stockResult as Record<string, unknown>;
    if (
      typedStockResult.command_id !== command.command_id
      || typedStockResult.command_digest !== digest
    ) {
      throw new Error('shipment_stock_response_identity_mismatch');
    }

    const validatedStockResult = validateStockCommandAckWritebackV1({
      envelope,
      result: typedStockResult,
    });
    await this.applyStockCommandWriteback(validatedStockResult);
    if (!existingFinalization) {
      await stockCommandSqliteStorage.transaction(async (tx) => {
        const raced = await tx.getFinalization(command.command_id);
        if (raced) {
          if (raced.digest !== digest || raced.outcome !== 'acknowledged') {
            throw new Error('shipment_stock_command_finalization_conflict');
          }
          return;
        }
        await tx.addFinalization({
          command_id: command.command_id,
          digest,
          outcome: 'acknowledged',
          reason: null,
          finalized_at: new Date().toISOString(),
        });
      });
    }
    await flushLocalDb();
    this.setState({ lastPushAt: new Date().toISOString() });
    try {
      await this.pull();
    } catch (pullError) {
      dbg.warn('mobile-sync', 'shipment_issue_supply_pull_failed', {
        shipment_id: shipmentId,
        error: toUserMessage(pullError),
      });
    }
    return {
      doc_number: docNumber,
      doc_year: docYear,
      stock_result: validatedStockResult,
    };
  }

  async prepareShipmentStockVoid(
    shipmentId: string,
  ): Promise<ShipmentStockVoidPreparationV1> {
    const { data, error } = await getSupabase().rpc(
      'prepare_shipment_stock_void_v1',
      { p_shipment_id: shipmentId },
    );
    if (error) throw error;
    const preparation = ShipmentStockVoidPreparationV1Schema.parse(data);
    if (
      preparation.shipment_id !== shipmentId
      || preparation.original_command_id !== shipmentId
    ) {
      throw new Error('shipment_stock_void_preparation_identity_mismatch');
    }
    return preparation;
  }

  /**
   * Atomically appends the receipt-bound reverse and marks an issued shipment
   * void. Call prepareShipmentStockVoid() first: its movements contain the
   * DB-authored original event ids and destination-side reversible limits.
   */
  async voidShipmentWithStock(
    shipmentId: string,
    reasonInput: string,
    commandInput: StockCommandV1,
  ): Promise<ShipmentStockVoidResponseV1> {
    const command = StockCommandV1Schema.parse(commandInput);
    const reason = reasonInput.trim();
    if (
      command.movement_class !== 'reverse'
      || command.reverses_command_id !== shipmentId
      || command.reason !== reason
    ) {
      throw new Error('shipment_stock_void_command_mismatch');
    }

    const existingFinalization = await stockCommandSqliteStorage.transaction(async (tx) => {
      const active = await tx.getCommand(command.command_id);
      if (active) throw new Error('shipment_stock_void_command_already_queued');
      return tx.getFinalization(command.command_id);
    });
    const digest = await stockCommandDigest(command);
    if (
      existingFinalization
      && (
        existingFinalization.digest !== digest
        || existingFinalization.outcome !== 'acknowledged'
      )
    ) {
      throw new Error('shipment_stock_void_finalization_conflict');
    }

    const deviceId = (await getMeta('device_id')) ?? 'unknown';
    const clientTs = new Date().toISOString();
    const events: SyncEventInput[] = materializeStockCommand(command).map((draft) => decorateSyncEventForOperator({
      client_uuid: crypto.randomUUID(),
      tenant_id: draft.tenant_id,
      business_id: draft.business_id,
      device_id: deviceId,
      client_ts: clientTs,
      kind: draft.kind,
      payload: draft.payload,
    }));
    const envelope = StockCommandEnvelopeV1Schema.parse({
      schema: 'trackeat.stock-command',
      version: 1,
      command,
      command_digest: digest,
      device_id: deviceId,
      events,
      master_effects: [],
    });

    const serverEnvelope = await prepareStockEnvelopeForServer(envelope);
    const { data, error } = await getSupabase().rpc(
      'void_shipment_with_stock_v1',
      {
        p_shipment_id: shipmentId,
        p_reason: reason,
        p_stock_command: serverEnvelope,
      },
    );
    if (error) throw error;
    const response = ShipmentStockVoidResponseV1Schema.parse(data);
    if (
      response.stock_result.command_id !== command.command_id
      || response.stock_result.command_digest !== digest
    ) {
      throw new Error('shipment_stock_void_response_identity_mismatch');
    }

    const validatedStockResult = validateStockCommandAckWritebackV1({
      envelope,
      result: response.stock_result,
      downstream: {
        kind: 'shipment_void',
        shipment_id: shipmentId,
      },
    });
    await this.applyStockCommandWriteback(validatedStockResult);
    if (!existingFinalization) {
      await stockCommandSqliteStorage.transaction(async (tx) => {
        const raced = await tx.getFinalization(command.command_id);
        if (raced) {
          if (raced.digest !== digest || raced.outcome !== 'acknowledged') {
            throw new Error('shipment_stock_void_finalization_conflict');
          }
          return;
        }
        await tx.addFinalization({
          command_id: command.command_id,
          digest,
          outcome: 'acknowledged',
          reason: null,
          finalized_at: new Date().toISOString(),
        });
      });
    }
    await flushLocalDb();
    this.setState({ lastPushAt: new Date().toISOString() });
    try {
      await this.pull();
    } catch (pullError) {
      dbg.warn('mobile-sync', 'shipment_void_supply_pull_failed', {
        shipment_id: shipmentId,
        error: toUserMessage(pullError),
      });
    }
    return response;
  }

  async voidShipmentWithoutStock(
    shipmentId: string,
    reasonInput: string,
  ): Promise<ShipmentWithoutStockVoidResponseV1> {
    const reason = reasonInput.trim();
    if (!reason) throw new Error('shipment_void_reason_required');
    const tenantId = await this.currentActiveTenantId();
    const deviceId = (await getMeta('device_id')) ?? 'unknown';
    const operatorAssertion = requireActiveOperatorAssertion();
    const { data, error } = await getSupabase().rpc(
      'void_shipment_without_stock_with_operator_v1',
      {
        p_shipment_id: shipmentId,
        p_reason: reason,
        p_device_id: deviceId,
        p_operator_assertion: operatorAssertion ?? null,
      },
    );
    if (error) throw error;
    const response = ShipmentWithoutStockVoidResponseV1Schema.parse(data);
    if (response.shipment_id !== shipmentId) {
      throw new Error('shipment_void_without_stock_response_identity_mismatch');
    }
    const validatedResponse =
      validateShipmentWithoutStockVoidWritebackV1({
        tenantId,
        shipmentId,
        reason,
        result: response,
      });
    await this.applyStockCommandWriteback(validatedResponse);
    await flushLocalDb();
    this.setState({ lastPushAt: new Date().toISOString() });
    try {
      await this.pull();
    } catch (pullError) {
      dbg.warn('mobile-sync', 'shipment_void_supply_pull_failed', {
        shipment_id: shipmentId,
        error: toUserMessage(pullError),
      });
    }
    return response;
  }

  async queueMaster(op: MasterOp, register?: RegisterLocalCommit): Promise<void> {
    await enqueueLegacyMasterOp(attachActiveOperatorAssertion(op), register);
  }

  async tick(): Promise<void> {
    return this.tickRerun.run(() => this.runTickOnce());
  }

  private async refreshOfflineState(): Promise<void> {
    const scope = this.offlineScope;
    const queueTenantId = this.offlineActiveTenantId();
    const pendingCount = await this.countAllPending();
    const queueAttentionCount = await readQueueAttentionCount(queueTenantId);
    const reconciliationCount = await countStockCommandReconciliations()
      + await countUnarchivedPermanentStockCommandRejections();
    if (scope !== this.offlineScope) return;
    // Losing connectivity does not resolve an earlier operational failure.
    const unresolvedError = this.consecutiveFailures > 0 && this.state.lastError !== null;
    this.setState({
      status: unresolvedError ? 'error' : 'offline',
      lastError: unresolvedError ? this.state.lastError : null,
      pendingCount,
      queueAttentionCount,
      reconciliationCount,
      queueTenantId,
    });
    // The health contract describes completed/error ticks, not connectivity.
    // Do not emit a false success or failure while waiting for the network.
  }

  private async runTickOnce(): Promise<void> {
    if (this.pausedForUpdate) return;
    const tickStart = performance.now();
    const scope = this.offlineScope;
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await this.refreshOfflineState();
        return;
      }
      await this.push();
      await this.pull();
      if (await requeueCleaningProductReferenceRejectionsOnce() > 0) {
        await this.push();
      }
      if (await requeueSharedTabletHaccpRejectionsOnce(await this.currentActiveTenantId()) > 0) {
        await this.push();
      }
      if (await requeueLegacyPrinterRejectionsOnce() > 0) {
        await this.push();
      }
      await this.checkDrift();
      const queueTenantId = await this.currentActiveTenantId();
      await resolveSupersededStockClosures(queueTenantId);
      await flushLocalDb();
      // I singoli drain aggiornano il badge durante il loro lavoro, ma una
      // busta atomica può scaricare più outbox (comando, RPC, esito stampa).
      // Alla fine di un tick riuscito il valore mostrato deve sempre venire
      // dalla somma reale, non dall'ultimo sotto-flusso che ha emesso uno stato.
      const pendingCount = await this.countAllPending();
      const queueAttentionCount = await readQueueAttentionCount(queueTenantId);
      const reconciliationCount = await countStockCommandReconciliations()
        + await countUnarchivedPermanentStockCommandRejections();
      this.consecutiveFailures = 0;
      // A dead-letter item needs operator attention, but it is no longer a
      // transport failure and must not keep the whole app in error forever.
      this.setState({
        status: 'idle',
        lastError: null,
        pendingCount,
        reconciliationCount,
        queueAttentionCount,
        queueTenantId,
      });
      dbg.info('mobile-sync', 'tick_ok', {
        ms: Math.round(performance.now() - tickStart),
        last_rev: (await getMeta('last_rev')) ?? '0',
        pending_count: pendingCount,
        attention_count: queueAttentionCount,
      });
      await emitSyncHealth({
        tenantId: queueTenantId,
        status: 'idle',
        pendingCount,
        attentionCount: queueAttentionCount,
        consecutiveFailures: 0,
        lastError: null,
        lastPullAt: this.state.lastPullAt,
        lastPushAt: this.state.lastPushAt,
      });
    } catch (err: unknown) {
      if (scope !== this.offlineScope) return;
      let failure = err;
      if (typeof navigator !== 'undefined' && !navigator.onLine
        && isSyncTransportUnavailableError(err)) {
        try {
          await this.refreshOfflineState();
          return;
        } catch (localError) {
          // A failed local queue read is still an operational error offline.
          failure = localError;
        }
      }
      const e = failure as Error;
      const lastError = toUserMessage(e, 'Sincronizzazione non riuscita');
      let queueTenantId = typeof navigator !== 'undefined' && !navigator.onLine
        ? scope?.tenantId ?? null : this.state.queueTenantId ?? null;
      let pendingCount = this.state.pendingCount;
      let queueAttentionCount = this.state.queueAttentionCount ?? 0;
      try {
        queueTenantId = typeof navigator !== 'undefined' && !navigator.onLine
          ? this.offlineActiveTenantId() : await this.currentActiveTenantId();
        pendingCount = await this.countAllPending();
        queueAttentionCount = await readQueueAttentionCount(queueTenantId);
      } catch {
        // Lo snapshot conserva l'ultimo stato noto; mai mascherare l'errore
        // originale con un fallimento della diagnostica.
      }
      if (scope !== this.offlineScope) return;
      this.consecutiveFailures += 1;
      this.setState({
        status: 'error',
        lastError,
        pendingCount,
        queueAttentionCount,
        ...(queueTenantId ? { queueTenantId } : {}),
      });
      dbg.error('mobile-sync', 'tick_failed', {
        ms: Math.round(performance.now() - tickStart),
        error_type: e.name,
        error_message: e.message,
        stack: e.stack?.split('\n').slice(0, 5).join('\n'),
      });
      if (queueTenantId) {
        await emitSyncHealth({
          tenantId: queueTenantId,
          status: 'error',
          pendingCount,
          attentionCount: queueAttentionCount,
          consecutiveFailures: this.consecutiveFailures,
          lastError,
          lastPullAt: this.state.lastPullAt,
          lastPushAt: this.state.lastPushAt,
        });
      }
    }
  }

  /**
   * Esegue una fetch con timeout, AbortController e logging granulare.
   * Distingue: TypeError (trasporto WebView non disponibile), AbortError
   * (timeout), HTTP 4xx/5xx (server response error), parse error, no-token.
   * Tutti loggati in debug_events locale (sopravvivono offline) e in
   * debug_traces server quando il flusher riesce a spedirli.
   */
  private async fetchSync<T>(
    name: string,
    init: { method: string; body?: string },
    parser: (j: unknown) => T,
    expectedRegisterScope?: PendingRegisterScope,
  ): Promise<T> {
    const url = this.functionUrl(name);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), SYNC_FETCH_TIMEOUT_MS);
    const start = performance.now();

    let token: string | null;
    try {
      const supabase = getSupabase();
      const { data, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) throw sessErr;
      if (expectedRegisterScope && (!data.session
        || data.session.user.id !== expectedRegisterScope.accountId
        || activeTenantFromJwt(data.session.access_token) !== expectedRegisterScope.tenantId
        || activeBusinessFromJwt(data.session.access_token) !== expectedRegisterScope.businessId
        || !isCurrentPersistedSession(data.session, SUPABASE_URL))) {
        throw new Error('register_sync_scope_changed');
      }
      if (expectedRegisterScope) assertCurrentRegisterScope(expectedRegisterScope);
      token = data.session?.access_token ?? null;
    } catch (err) {
      clearTimeout(timer);
      const e = err as Error;
      dbg.error('mobile-sync', `${name}_session_failed`, {
        error_type: e.name,
        error_message: e.message,
      });
      throw e;
    }
    if (!token) {
      clearTimeout(timer);
      dbg.warn('mobile-sync', `${name}_no_token`, { online: navigator.onLine });
      throw new Error(`${name}: not_authenticated`);
    }

    let res: Response;
    try {
      res = await fetchSyncReadTransport(url, {
        method: init.method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: init.body,
        signal: ctrl.signal,
      }, () => {
        const current = readPersistedSession(SUPABASE_URL);
        if (!current || current.access_token !== token) throw new Error('register_sync_scope_changed');
        if (expectedRegisterScope) assertCurrentRegisterScope(expectedRegisterScope);
      });
    } catch (err) {
      clearTimeout(timer);
      const ms = Math.round(performance.now() - start);
      const e = err as Error;
      // A failed fetch alone cannot distinguish connectivity, DNS/TLS or WebView failure.
      const kind = ctrl.signal.aborted ? 'timeout' : 'transport_unavailable';
      dbg.error('mobile-sync', `${name}_fetch_failed`, {
        url,
        ms,
        kind,
        online: navigator.onLine,
        error_type: e.name,
        error_message: e.message,
        // primi 3 livelli di stack per orientarsi (utile lato dev)
        stack: e.stack?.split('\n').slice(0, 3).join('\n'),
      });
      throw e;
    }
    clearTimeout(timer);
    const ms = Math.round(performance.now() - start);

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      dbg.error('mobile-sync', `${name}_http_${res.status}`, {
        url,
        ms,
        status: res.status,
        body: text.slice(0, 500),
      });
      throw new Error(`${name} http ${res.status}`);
    }

    let json: unknown;
    try {
      json = await res.json();
    } catch (err) {
      const e = err as Error;
      dbg.error('mobile-sync', `${name}_parse_failed`, {
        url, ms, status: res.status, error_message: e.message,
      });
      throw e;
    }

    if (expectedRegisterScope) assertCurrentRegisterScope(expectedRegisterScope);
    let parsed: T;
    try {
      parsed = parser(json);
    } catch (err) {
      const e = err as Error;
      dbg.error('mobile-sync', `${name}_schema_failed`, {
        url, ms, error_message: e.message,
      });
      throw e;
    }

    dbg.info('mobile-sync', `${name}_ok`, { url, ms, status: res.status });
    return parsed;
  }

  /**
   * Anti-drift: chiede al server l'head_rev attuale e lo confronta col
   * last_rev locale. Se differenza > 0 e l'ultimo pull è vecchio, logga
   * `drift_detected` con severity error per allertare lo sviluppatore.
   * Niente UI per l'utente: deciso di tenere il sync invisibile.
   */
  async checkDrift(): Promise<void> {
    try {
      const localRev = Number.parseInt((await getMeta('last_rev')) ?? '0', 10) || 0;
      const head = await this.fetchSync(
        'sync-head',
        { method: 'POST', body: JSON.stringify({}) },
        (j) => {
          const o = j as { head_rev?: number; server_time?: string };
          return { head_rev: o.head_rev ?? 0, server_time: o.server_time ?? null };
        },
      );
      const lastPullMs = this.state.lastPullAt ? Date.parse(this.state.lastPullAt) : 0;
      const ageMs = lastPullMs > 0 ? Date.now() - lastPullMs : Number.POSITIVE_INFINITY;
      const drift = head.head_rev - localRev;
      if (drift > 0 && ageMs > DRIFT_STALE_THRESHOLD_MS) {
        dbg.error('mobile-sync', 'drift_detected', {
          local_rev: localRev,
          head_rev: head.head_rev,
          drift,
          last_pull_at: this.state.lastPullAt,
          minutes_behind: Math.round(ageMs / 60_000),
        });
      } else if (drift > 0) {
        dbg.info('mobile-sync', 'drift_minor', {
          local_rev: localRev,
          head_rev: head.head_rev,
          drift,
        });
      }
    } catch (err) {
      // checkDrift è best-effort: non vogliamo che un suo fail blocchi tick().
      const e = err as Error;
      dbg.warn('mobile-sync', 'drift_check_failed', { error: e.message });
    }
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const supabase = getSupabase();
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Non autenticato');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private functionUrl(name: string): string {
    // Trim trailing slash if present; sync-pull / sync-push mirror Supabase Edge Functions convention.
    const base = SUPABASE_URL.replace(/\/$/, '');
    return `${base}/functions/v1/${name}`;
  }

  private async currentActiveTenantId(): Promise<string> {
    const { data, error } = await getSupabase().auth.getSession();
    if (error) throw error;
    const token = data.session?.access_token;
    if (!token) throw new Error('atomic_rpc_not_authenticated');
    const tenantId = activeTenantFromJwt(token);
    if (!tenantId) throw new Error('atomic_rpc_active_tenant_missing');
    return tenantId;
  }

  private async pushAtomicRpcKind(
    tenantId: string,
    kind: AtomicRpcKind,
    shouldPush: (record: AtomicRpcRecord) => boolean = () => true,
  ): Promise<void> {
    const deferredRequestIds = new Set<string>();
    for (let chunk = 0; chunk < MAX_PUSH_CHUNKS_PER_TICK; chunk += 1) {
      const records = await listPendingAtomicRpcsV1(
        tenantId,
        this.pushBatchSize,
        kind,
        false,
        deferredRequestIds,
      );
      if (records.length === 0) break;
      for (const skipped of records.filter((record) => !shouldPush(record))) {
        deferredRequestIds.add(skipped.request_id);
      }
      for (const pending of records.filter(shouldPush)) {
        const handle: AtomicRpcHandle = {
          request_id: pending.request_id,
          kind: pending.kind,
          tenant_id: pending.tenant_id,
          scope_key: pending.scope_key,
          digest: pending.digest,
        };
        try {
          const record = await beginAtomicRpcSendV1(handle);
          const splitRequest = splitOperatorAssertion(record.request);
          const deviceId = (await getMeta('device_id')) ?? 'unknown';
          await activateOperatorAssertionForDevice(
            splitRequest.assertion,
            deviceId,
          );
          const serverRequest = splitRequest.value;
          const serverRecord = { ...record, request: serverRequest };
          let data: unknown;
          let rpcError: unknown = null;
          if (kind === 'production_plan_replace_v1') {
            const response = await getSupabase().rpc(
              'replace_production_plan_v1',
              { p_request: serverRequest },
            );
            data = response.data;
            rpcError = response.error;
          } else if (kind === 'stock_reconciliation_v1') {
            const response = await getSupabase().rpc(
              'apply_stock_reconciliation_v2',
              { p_request: serverRequest },
            );
            data = response.data;
            rpcError = response.error;
          } else if (kind === 'consultant_historical_lot_recount_v1') {
            const response = await getSupabase().rpc(
              'apply_consultant_historical_lot_recount_v1',
              { p_request: serverRequest },
            );
            data = response.data;
            rpcError = response.error;
          } else if (kind === 'food_supplier_match_archive_v1') {
            const response = await getSupabase().rpc(
              'archive_food_supplier_matches_v1',
              { p_request: serverRequest },
            );
            data = response.data;
            rpcError = response.error;
          } else if (kind === 'supplier_item_match_management_v1') {
            const response = await getSupabase().rpc(
              'manage_supplier_item_match_v1',
              { p_request: serverRequest },
            );
            data = response.data;
            rpcError = response.error;
          } else {
            const response = await getSupabase().rpc(
              'apply_production_day_transition_v1',
              { p_transition: serverRequest },
            );
            data = response.data;
            rpcError = response.error;
          }
          if (rpcError) {
            const reason = atomicRpcFailureReason(rpcError);
            if (isRetryableAtomicRpcError(rpcError)) {
              throw rpcError;
            }
            const terminalConflict = {
              code:
                typeof rpcError === 'object'
                  && rpcError
                  && typeof (rpcError as { code?: unknown }).code === 'string'
                  ? (rpcError as { code: string }).code
                  : 'atomic_rpc_terminal_rejection',
              reason,
              details: {
                code:
                  typeof rpcError === 'object' && rpcError
                    ? (rpcError as { code?: unknown }).code ?? null
                    : null,
                message: reason,
              },
            };
            await markAtomicRpcConflictCascadeV1(handle, terminalConflict);
            continue;
          }

          try {
            if (kind === 'production_plan_replace_v1') {
              await acknowledgeProductionPlanReplaceResultV1(
                serverRecord as AtomicRpcRecord<ProductionPlanReplaceRequestV1>,
                data,
              );
            } else if (kind === 'stock_reconciliation_v1') {
              parseStockReconciliationResultV1(
                data,
                serverRequest as StockReconciliationRequestV1,
                record.digest,
              );
              await acknowledgeAtomicRpcV1(handle);
            } else if (kind === 'consultant_historical_lot_recount_v1') {
              parseConsultantHistoricalLotRecountResultV1(
                data,
                serverRequest as ConsultantHistoricalLotRecountRequestV1,
                record.digest,
              );
              await acknowledgeAtomicRpcV1(handle);
            } else if (kind === 'food_supplier_match_archive_v1') {
              parseFoodSupplierMatchArchiveResultV1(
                data,
                serverRequest as FoodSupplierMatchArchiveRequestV1,
                record.digest,
              );
              await acknowledgeAtomicRpcV1(handle);
            } else if (kind === 'supplier_item_match_management_v1') {
              parseSupplierItemMatchManagementResultV1(
                data,
                serverRequest as SupplierItemMatchManagementRequestV1,
                record.digest,
              );
              await acknowledgeAtomicRpcV1(handle);
            } else {
              const request = serverRequest as unknown as
                ProductionDayTransitionRequestV1;
              const result = parseProductionDayTransitionResultV1(
                data,
                request,
                record.digest,
              );
              await applyPulledMasterToStockCommandCache(
                {
                  table: 'production_days',
                  id: result.production_day.id,
                  version: result.production_day.version,
                  row: result.production_day,
                },
                {
                  excludeAtomicRequestId: record.request_id,
                  trustedAtomicReceipt: {
                    requestId: record.request_id,
                    digest: record.digest,
                  },
                },
              );
              await acknowledgeAtomicRpcV1(handle);
            }
          } catch (writebackError) {
            // The server may already have committed and retained its receipt. Never
            // roll back the optimistic layer on an untrusted/malformed ACK: persist
            // the same request and retry until a semantically valid receipt arrives.
            if (isSyncTransportUnavailableError(writebackError)) {
              throw writebackError;
            }
            await retryAtomicRpcV1(
              handle,
              `writeback_failed:${atomicRpcFailureReason(writebackError)}`,
            ).catch(() => undefined);
            deferredRequestIds.add(handle.request_id);
            dbg.warn('mobile-sync', 'atomic_rpc_writeback_deferred', {
              request_id: handle.request_id,
              kind: handle.kind,
              error: atomicRpcFailureReason(writebackError),
            });
            continue;
          }
          await flushLocalDb();
          this.setState({
            lastPushAt: new Date().toISOString(),
            pendingCount: await this.countAllPending(),
          });
        } catch (processingError) {
          const reason = atomicRpcFailureReason(processingError);
          await retryAtomicRpcV1(handle, reason).catch(() => undefined);
          if (isSyncTransportUnavailableError(processingError)) {
            throw processingError;
          }
          deferredRequestIds.add(handle.request_id);
          dbg.warn('mobile-sync', 'atomic_rpc_retry_deferred', {
            request_id: handle.request_id,
            kind: handle.kind,
            reason,
          });
        }
      }
    }
  }

  async push(): Promise<SyncPushResponse | null> {
    let lastJson: SyncPushResponse | null = null;
    const deferredPendingKeys = new Set<string>();
    const deferPending = (pending: PendingOpRow): void => {
      deferredPendingKeys.add(pendingOpIsolationKeyV1(pending));
    };

    // Le prove fotografiche devono arrivare nel bucket privato prima del loro
    // evento: listRunnablePendingOps mantiene bloccato il JSON finche la riga
    // foto locale esiste. Il drain qui garantisce l'ordine anche al reconnect.
    const evidenceTenantId = await this.currentActiveTenantId();
    const evidenceDrain = await drainOperationalEvidenceOutbox(evidenceTenantId);
    if (evidenceDrain.failed > 0) {
      dbg.warn('mobile-sync', 'operational_evidence_upload_failed', {
        tenant_id: evidenceTenantId,
        failed: evidenceDrain.failed,
      });
    } else if (evidenceDrain.uploaded > 0) {
      dbg.info('mobile-sync', 'operational_evidence_uploaded', {
        tenant_id: evidenceTenantId,
        uploaded: evidenceDrain.uploaded,
      });
    }

    // Drain the outbox in bounded chunks. A single oversized request (e.g. a
    // local-data reset re-queues hundreds of ops) can take longer than the fetch
    // timeout, abort, and never apply the server's response — wedging the queue
    // forever. Each chunk stays well under SYNC_FETCH_TIMEOUT_MS; chunks that
    // drain cleanly chain so an idempotent backlog clears within one tick.
    for (let chunk = 0; chunk < MAX_PUSH_CHUNKS_PER_TICK; chunk += 1) {
      const registerScope = currentRegisterScope();
      const candidates = await listRunnablePendingOps(
        this.pushBatchSize,
        deferredPendingKeys,
        registerScope,
      );
      const validCandidates = [] as typeof candidates;
      const selectedPendingKeys = new Set<string>();
      for (const candidate of candidates) {
        const parsed = candidate.kind === 'event'
          ? SyncEventInputSchema.safeParse(candidate.payload)
          : MasterOpSchema.safeParse(candidate.payload);
        if (!parsed.success) {
          const issue = parsed.error.issues[0];
          const reason = candidate.invalidPayloadReason
            ?? `client_payload_invalid:${issue?.path.join('.') || 'root'}:${issue?.code ?? 'unknown'}`;
          await quarantinePendingOp(candidate, reason);
          dbg.warn('mobile-sync', 'invalid_pending_op_quarantined', {
            pending_op_id: candidate.id,
            kind: candidate.kind,
            reason,
          });
          continue;
        }
        const isolationKey = pendingOpIsolationKeyV1(candidate);
        if (selectedPendingKeys.has(isolationKey)) continue;
        selectedPendingKeys.add(isolationKey);
        validCandidates.push(candidate);
      }
      const pending = boundPendingPushBytes(validCandidates);
      if (pending.length === 0) {
        if (candidates.length === 0) {
          this.setState({ pendingCount: await this.countAllPending() });
          break;
        }
        continue;
      }
      // Show the true backlog (capped at batchSize), not just this chunk's size.
      const totalPending = await this.countLegacyPending()
        + await countPendingStockCommands()
        + await countPendingProductionLabelPrintOutcomes();
      this.setState({ status: 'pushing', pendingCount: totalPending });

      const body = buildLegacyPendingPushRequest(pending);
      if (registerScope?.operator && pending.some((row) => row.ownerAccountId)) {
        assertCurrentRegisterScope(registerScope);
        // The actor, facts, UUID and stored intent stay unchanged. A renewed
        // grant is transport authorization for the same original operator.
        const operator = registerScope.operator;
        body.master_ops = body.master_ops?.map((op) => isOfflineRegisterTable(op.table)
          ? { ...op, operator_assertion: {
            grant_id: operator.grantId, grant_token: operator.grantToken,
            tenant_personnel_id: operator.tenantPersonnelId,
          } }
          : op);
      }

      const json = await this.fetchSync(
        'sync-push',
        { method: 'POST', body: JSON.stringify(body) },
        (j) => SyncPushResponseSchema.parse(j),
        pending.some((row) => row.ownerAccountId) ? registerScope : undefined,
      );
      lastJson = json;

      const acceptedEventUuids = new Set(json.accepted_events);
      const acceptedMasterIds = new Set(json.accepted_masters);
      const rejectedEventUuids = new Map(json.rejected_events.map((r) => [r.client_uuid, r.reason]));
      const rejectedMasterIds = new Map(json.rejected_masters.map((r) => [r.id, r.reason]));
      // Server-resolved conflicts (lww_server). The server keeps its value and
      // returns it here precisely so the client can adopt it. Without this the
      // local row keeps a stale base_version and the same op is re-pushed every
      // tick forever (each producing a spurious sync_conflict event). Keyed by
      // table:id because master ops are matched on the (table,id) pair.
      const conflictsByKey = new Map(
        json.conflicts.map((c) => [`${c.table}:${c.id}`, c] as const),
      );
      const db = json.conflicts.length > 0 ? await openLocalDb(null) : null;

      for (const p of pending) {
        if (p.ownerAccountId && registerScope) assertCurrentRegisterScope(registerScope);
        if (p.kind === 'event') {
          const evt = p.payload as SyncEventInput;
          if (acceptedEventUuids.has(evt.client_uuid)) {
            await deletePendingOp(p.id);
          } else if (rejectedEventUuids.has(evt.client_uuid)) {
            const reason = rejectedEventUuids.get(evt.client_uuid) ?? 'rejected';
            if (
              isLegacyDirectStockEvent(evt)
              && isTerminalLegacyStockRejection(reason)
            ) {
              try {
                await quarantineLegacyStockPendingOp({
                  pending: p,
                  event: evt,
                  reason,
                });
                dbg.warn('mobile-sync', 'legacy_stock_quarantined', {
                  client_uuid: evt.client_uuid,
                  kind: evt.kind,
                  reason,
                });
              } catch (error) {
                await markPendingOpFailed(
                  p.id,
                  `legacy_stock_quarantine_failed:${toUserMessage(error)}`,
                );
                deferPending(p);
              }
              continue;
            }
            // Upgrade safety: legacy direct-stock events already have an
            // optimistic projection. Never erase them on a rejection; the
            // request builder routes them through the retained legacy server
            // branch, and a transient/mixed-version rejection stays durable.
            if (
              !isLegacyDirectStockEvent(evt)
              && shouldQuarantineRejectedSyncOp(reason)
            ) {
              dbg.warn('mobile-sync', 'terminal_event_quarantined', {
                client_uuid: evt.client_uuid, kind: evt.kind, attempts: p.attempts + 1, reason,
              });
              await quarantinePendingOp(p, reason);
            } else {
              await markPendingOpFailed(p.id, reason);
              deferPending(p);
            }
          } else {
            await markPendingOpFailed(p.id, 'sync_push_event_not_acknowledged');
            deferPending(p);
          }
        } else {
          const m = p.payload as MasterOp;
          const conflict = conflictsByKey.get(`${m.table}:${m.id}`);
          if (conflict && isOfflineRegisterTable(m.table) && conflict.resolution === 'manual') {
            // Insert-only registers cannot adopt a different row with the same
            // UUID: retain the rejected intent for the account that saved it.
            await quarantinePendingOp(p, rejectedMasterIds.get(m.id) ?? 'insert_id_conflict');
          } else if (conflict) {
            // LWW: the server's value won and is authoritative. Adopt it into
            // masters_local (so the version is corrected and the UI shows the
            // truth) and drop our losing op — retrying the same base_version can
            // never succeed, so this is terminal, not a retryable rejection.
            try {
              if (!db) throw new Error('master_conflict_db_unavailable');
              await this.adoptServerMaster(
                db,
                conflict.table,
                conflict.id,
                conflict.server_value,
              );
            } catch (err) {
              // Non eliminare l'op finché la copia server non è davvero nel
              // mirror locale. Altrimenti un'emissione concorrente potrebbe
              // scambiare la bozza ottimistica stantia per un ACK riuscito.
              await markPendingOpFailed(
                p.id,
                `master_conflict_adopt_failed:${toUserMessage(err)}`,
              );
              dbg.warn('mobile-sync', 'master_conflict_adopt_failed', {
                table: conflict.table,
                id: conflict.id,
                error: toUserMessage(err),
              });
              deferPending(p);
              continue;
            }
            dbg.warn('mobile-sync', 'master_conflict_resolved_lww_server', {
              table: conflict.table, id: conflict.id,
              server_version: (conflict.server_value as { version?: number }).version ?? null,
            });
            await deletePendingOp(p.id);
          } else if (acceptedMasterIds.has(m.id)) {
            await deletePendingOp(p.id);
          } else if (rejectedMasterIds.has(m.id)) {
            const reason = rejectedMasterIds.get(m.id) ?? 'rejected';
            const registerGrantNeedsRenewal = isOfflineRegisterTable(m.table)
              && reason === 'shared_tablet_operator_grant_invalid';
            if (!registerGrantNeedsRenewal && shouldQuarantineRejectedSyncOp(reason)) {
              dbg.warn('mobile-sync', 'terminal_master_quarantined', {
                master_id: m.id, table: m.table, attempts: p.attempts + 1, reason,
              });
              await quarantinePendingOp(p, reason);
            } else {
              await markPendingOpFailed(p.id, reason);
              deferPending(p);
            }
          } else {
            await markPendingOpFailed(p.id, 'sync_push_master_not_acknowledged');
            deferPending(p);
          }
        }
      }

      // Ricalcola il counter reale dopo il drain di questo chunk.
      this.setState({
        lastPushAt: new Date().toISOString(),
        pendingCount: await this.countAllPending(),
      });

      // Retryable rows are excluded only for this invocation. Independent rows
      // behind them keep draining; the failed scope remains durable for the
      // next tick and visible in queue diagnostics.
      if ((await listRunnablePendingOps(
        1,
        deferredPendingKeys,
        currentRegisterScope(),
      )).length === 0) break;
    }

    // Drain every sendable legacy prerequisite first. A retryable legacy scope
    // is excluded for this invocation only: unrelated atomic queues may proceed,
    // while explicit prerequisite markers still hold their dependants locally.
    if ((await listRunnablePendingOps(1, deferredPendingKeys, currentRegisterScope())).length === 0) {
      const activeTenantId = await this.currentActiveTenantId();
      // Global causal order:
      // older legacy prerequisites -> reopen -> same-day production plan ->
      // dependent StockCommands -> production-day close/verify. A later local
      // production edit remains marker-blocked until reopen ACK.
      await this.pushAtomicRpcKind(
        activeTenantId,
        'production_day_transition_v1',
        (record) => (
          (record.request as { transition?: unknown }).transition === 'reopen'
        ),
      );
      // Reopen ACK releases legacy production edits that were durably marker-
      // blocked. Stop this tick so the next pass drains those masters before a
      // later close/verify can re-freeze the same day.
      if (mustYieldForLegacyAfterReopenAckV1(
        (await listRunnablePendingOps(1, deferredPendingKeys, currentRegisterScope())).length,
      )) {
        return lastJson;
      }
      await this.pushAtomicRpcKind(
        activeTenantId,
        'production_plan_replace_v1',
      );
      await this.pushAtomicRpcKind(
        activeTenantId,
        'stock_reconciliation_v1',
      );
      await this.pushAtomicRpcKind(
        activeTenantId,
        'food_supplier_match_archive_v1',
      );
      await this.pushAtomicRpcKind(
        activeTenantId,
        'supplier_item_match_management_v1',
      );
      const commandJson = await this.pushStockCommands(activeTenantId);
      if (commandJson) lastJson = commandJson;
      // A historical recovery is a physical count over the state that was
      // already queued when the consultant opened it.  Draining ordinary
      // stock commands first prevents a stale count from leapfrogging a
      // previous receipt; the server still compares the full snapshot.
      await this.pushAtomicRpcKind(
        activeTenantId,
        'consultant_historical_lot_recount_v1',
      );
      await this.pushProductionLabelPrintOutcomes();
      if (
        // Terminal dependency conflicts have already had their optimistic
        // facts rolled back and are not sendable. They remain surfaced for
        // reconciliation, but must not freeze unrelated day transitions
        // tenant-wide.
        await countPendingStockCommands(activeTenantId) === 0
        && await countPendingProductionLabelPrintOutcomes() === 0
      ) {
        await this.pushAtomicRpcKind(
          activeTenantId,
          'production_day_transition_v1',
          (record) => (
            (record.request as { transition?: unknown }).transition
            !== 'reopen'
          ),
        );
      }
    }

    return lastJson;
  }

  private async pushStockCommands(
    tenantId: string,
    deferredCommandIds = new Set<string>(),
    chunksRemaining = MAX_PUSH_CHUNKS_PER_TICK,
  ): Promise<SyncPushResponse | null> {
    if (chunksRemaining <= 0) return null;
    const queuedBatches = await listPendingStockCommandBatches(
      20,
      tenantId,
      deferredCommandIds,
    );
    const readiness = await Promise.all(queuedBatches.map((batch) => (
      isProductionLabelCommandReadyForPush(
        batch.record.command_id,
        batch.record.master_effects,
      )
    )));
    const candidates = queuedBatches.filter((_, index) => readiness[index]);
    for (const batch of queuedBatches) {
      deferredCommandIds.add(batch.record.command_id);
    }
    if (candidates.length === 0) {
      return this.pushStockCommands(
        tenantId,
        deferredCommandIds,
        chunksRemaining - 1,
      );
    }

    this.setState({
      status: 'pushing',
      pendingCount: candidates.length + await this.countLegacyPending(),
    });
    const fallbackDeviceId = (await getMeta('device_id')) ?? 'unknown';
    const prepared = await prepareStockCommandPush(candidates, async (batch) => (
      batch.events.length === 0
        ? await masterOnlyEnvelopeOperator({
            commandId: batch.record.command_id,
            fallbackDeviceId,
          })
        : { deviceId: batch.events[0]?.device_id ?? fallbackDeviceId, assertion: undefined }
    ));
    if (prepared.length === 0) return null;
    const batches = prepared.map((item) => item.batch);
    const envelopes = prepared.map((item) => item.envelope);
    const envelopeByCommandId = new Map(envelopes.map((envelope) => [
      envelope.command.command_id,
      envelope,
    ] as const));
    const body: SyncPushRequest = {
      sync_protocol: 1,
      client_version:
        (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown',
      client_capabilities: [
        'stock_command_v1',
        'stock_command_dead_letter_v1',
        'sync_poison_isolation_v1',
      ],
      events: [],
      master_ops: [],
      stock_commands_v1: envelopes,
    };

    let json: SyncPushResponse;
    try {
      json = await this.fetchSync(
        'sync-push',
        { method: 'POST', body: JSON.stringify(body) },
        (value) => SyncPushResponseSchema.parse(value),
      );
    } catch (error) {
      const reason = toUserMessage(error, 'Invio comando magazzino non riuscito');
      for (const batch of batches) {
        await retryStockCommand(
          stockCommandSqliteStorage,
          batch.handle,
          reason,
        ).catch(() => undefined);
      }
      throw error;
    }

    const accepted = new Map(
      json.accepted_stock_commands.map((item) => [item.command_id, item] as const),
    );
    const rejected = new Map(
      json.rejected_stock_commands.map((item) => [item.command_id, item] as const),
    );

    for (const batch of batches) {
      const ack = accepted.get(batch.record.command_id);
      if (ack) {
        if (ack.command_digest !== batch.record.digest) {
          await retryStockCommand(
            stockCommandSqliteStorage,
            batch.handle,
            'server_command_digest_mismatch',
          );
          continue;
        }
        try {
          const envelope = envelopeByCommandId.get(
            batch.record.command.command_id,
          );
          if (!envelope) {
            throw new Error('stock_command_envelope_not_found');
          }
          const validatedResult = validateStockCommandAckWritebackV1({
            envelope,
            result: ack.result,
          });
          await acknowledgeStockCommandWriteback(
            batch.handle,
            validatedResult,
          );
        } catch (error) {
          await retryStockCommand(
            stockCommandSqliteStorage,
            batch.handle,
            `writeback_failed:${atomicRpcFailureReason(error)}`,
          );
        }
        continue;
      }

      const rejection = rejected.get(batch.record.command_id);
      if (!rejection) {
        await retryStockCommand(
          stockCommandSqliteStorage,
          batch.handle,
          'stock_command_not_acknowledged',
        );
      } else if (rejection.retryable) {
        await retryStockCommand(
          stockCommandSqliteStorage,
          batch.handle,
          rejection.reason,
        );
      } else {
        await rejectStockCommandPermanently(
          stockCommandSqliteStorage,
          batch.handle,
          { permanent: true, reason: rejection.reason },
        );
      }
    }
    await reconcileRejectedProductionLabelPrintOutcomes();

    this.setState({
      lastPushAt: new Date().toISOString(),
      pendingCount: await countPendingStockCommands()
        + await this.countLegacyPending(),
    });
    const later = await this.pushStockCommands(
      tenantId,
      deferredCommandIds,
      chunksRemaining - 1,
    );
    return later ?? json;
  }

  private async pushProductionLabelPrintOutcomes(): Promise<void> {
    const deferredIntentIds = new Set<string>();
    for (let chunk = 0; chunk < MAX_PUSH_CHUNKS_PER_TICK; chunk += 1) {
      const outcomes = await listPendingProductionLabelPrintOutcomes(
        20,
        deferredIntentIds,
      );
      if (outcomes.length === 0) break;
      for (const outcome of outcomes) {
        deferredIntentIds.add(outcome.intent_id);
        try {
          await beginProductionLabelPrintOutcomeSend(outcome.intent_id);
          const deviceId = (await getMeta('device_id')) ?? 'unknown';
          await activateOperatorAssertionForDevice(
            outcome.operator_assertion,
            deviceId,
          );
          const { data, error } = await getSupabase().rpc(
            'finalize_production_label_print_intent_v2',
            {
              p_intent_id: outcome.intent_id,
              p_outcome: outcome.outcome,
              p_occurred_at: outcome.occurred_at,
              p_error: outcome.error,
              p_device_id: deviceId,
            },
          );
          if (error) throw error;
          if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('production_label_print_outcome_response_invalid');
          }
          const response = data as Record<string, unknown>;
          if (
            response.schema !== 'trackeat.production-label-print-outcome'
            || response.version !== 1
            || response.intent_id !== outcome.intent_id
            || response.outcome !== outcome.outcome
          ) {
            throw new Error('production_label_print_outcome_identity_mismatch');
          }
          const validatedResponse =
            validateProductionLabelOutcomeWritebackV1({
              pending: outcome,
              deviceId,
              result: response,
            });
          await this.applyStockCommandWriteback(validatedResponse);
          await acknowledgeProductionLabelPrintOutcome(outcome.intent_id);
        } catch (error) {
          if (await this.adoptConfirmedLabelPrintReplay(outcome, error)) {
            continue;
          }
          await retryProductionLabelPrintOutcome(
            outcome.intent_id,
            toUserMessage(error, 'Esito stampa non sincronizzato'),
          ).catch(() => undefined);
          if (isSyncTransportUnavailableError(error)) throw error;
          dbg.warn('mobile-sync', 'label_print_outcome_retry_deferred', {
            intent_id: outcome.intent_id,
            error: toUserMessage(error),
            reason: atomicRpcFailureReason(error),
          });
        }
      }
      this.setState({
        lastPushAt: new Date().toISOString(),
        pendingCount:
          await this.countLegacyPending()
          + await countPendingStockCommands()
          + await countPendingProductionLabelPrintOutcomes(),
      });
    }
  }

  private async adoptConfirmedLabelPrintReplay(
    outcome: PendingProductionLabelPrintOutcome,
    error: unknown,
  ): Promise<boolean> {
    if (!isProductionLabelOutcomeReplayConflict(error)) return false;
    try {
      const { data, error: listError } = await getSupabase().rpc(
        'list_production_label_print_intents_v1',
        {
          p_business_id: outcome.business_id,
          p_states: [outcome.outcome],
        },
      );
      if (listError) throw listError;
      if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
      const response = data as Record<string, unknown>;
      if (
        response.schema !== 'trackeat.production-label-print-intent-list'
        || response.version !== 1
        || response.tenant_id !== outcome.tenant_id
        || !Array.isArray(response.items)
      ) {
        return false;
      }
      const remote = response.items.find((item) => (
        item !== null
        && typeof item === 'object'
        && !Array.isArray(item)
        && (item as Record<string, unknown>).id === outcome.intent_id
      ));
      if (!remote) return false;
      const adopted = await adoptConfirmedProductionLabelPrintOutcome({
        pending: outcome,
        remoteIntent: remote,
      });
      if (!adopted) return false;
      await flushLocalDb();
      dbg.warn('mobile-sync', 'label_print_outcome_adopted_after_replay_conflict', {
        intent_id: outcome.intent_id,
        outcome: outcome.outcome,
      });
      return true;
    } catch (recoveryError) {
      dbg.warn('mobile-sync', 'label_print_replay_recovery_failed', {
        intent_id: outcome.intent_id,
        error: toUserMessage(recoveryError),
      });
      return false;
    }
  }

  private async applyStockCommandWriteback(result: Record<string, unknown>): Promise<void> {
    await applyStockCommandWritebackLocally(result);
  }

  /**
   * Push a SINGLE queued master op straight to the server and VERIFY the server
   * accepted THAT specific row — bypassing the full `tick()` (no pull, no drift)
   * and the FIFO outbox drain.
   *
   * Why this exists (label-print QR reliability): the productions row that
   * carries a label's `trace_token` must be resolvable server-side the instant
   * the QR is printed, otherwise the trace-page shows "Lavorazione non trovata".
   * The old approach (`await tick()` raced against a timeout) was unsound:
   *   - before the rerun latch, a concurrent tick wake-up was dropped while the
   *     fire-and-forget tick from `useEnqueueMaster` was already running, so the
   *     awaited call could complete having pushed nothing → false "synced";
   *   - the production op sits at the OUTBOX TAIL, so a stuck op ahead of it could
   *     block the push chunk before it was ever sent;
   *   - "tick didn't throw" was treated as "the row landed", even when the row was
   *     rejected → a silently dead QR with no warning.
   * This method is immune to all three: it bypasses the general tick drain, it
   * targets exactly one row regardless of backlog depth/order, and it inspects
   * `accepted_masters` for THIS id before returning success.
   *
   * Returns `confirmed:true` iff the server accepted the op (or it already exists
   * → idempotent re-push via the 23505 path). On confirm it drops the now-redundant
   * queued copy so the periodic tick doesn't re-send it. Returns `confirmed:false`
   * with a reason for offline / rejection (`tenant_mismatch`, `insert_failed:*`,
   * trial-block 403, …) / timeout — the caller surfaces this instead of printing a
   * confidently dead QR. The row stays queued in those cases so the background tick
   * keeps retrying (offline-first eventual consistency).
   */
  async confirmMasterPushed(
    table: string,
    id: string,
    opts: {
      timeoutMs?: number;
      acceptConflict?: boolean;
      expectedPatch?: Readonly<Record<string, unknown>>;
    } = {},
  ): Promise<{ confirmed: boolean; reason?: string }> {
    if (!navigator.onLine) return { confirmed: false, reason: 'offline' };
    const match = await findPendingMasterOp(table, id);
    if (!match) {
      // L'op può essere stata drenata da un tick concorrente. I flussi che
      // tollerano LWW conservano il comportamento storico; emissioni fiscali e
      // stock sono fail-closed e devono provare che nel mirror ci sia ancora
      // esattamente il payload ottimistico atteso. Il tick adotta la copia
      // server PRIMA di eliminare una op in conflitto.
      if (opts.acceptConflict !== false) return { confirmed: true };
      if (!opts.expectedPatch) {
        return { confirmed: false, reason: 'master_confirmation_unverifiable' };
      }
      try {
        const db = await openLocalDb(null);
        const result = await db.query(
          `SELECT row
             FROM masters_local
            WHERE table_name = ?
              AND id = ?
            LIMIT 1`,
          [table, id],
        );
        const raw = (
          result.values?.[0] as { row?: string } | undefined
        )?.row;
        if (!raw) {
          return { confirmed: false, reason: 'master_confirmation_unverifiable' };
        }
        const localRow = JSON.parse(raw) as unknown;
        if (!masterRowContainsExpectedPatch(localRow, opts.expectedPatch)) {
          dbg.warn('mobile-sync', 'confirm_master_drained_payload_mismatch', {
            table,
            id,
          });
          return { confirmed: false, reason: 'master_version_conflict' };
        }
        dbg.info('mobile-sync', 'confirm_master_ok', {
          table,
          id,
          via: 'concurrent_drain_verified',
        });
        return { confirmed: true };
      } catch (err) {
        dbg.warn('mobile-sync', 'confirm_master_drained_verify_failed', {
          table,
          id,
          error: toUserMessage(err),
        });
        return { confirmed: false, reason: 'master_confirmation_unverifiable' };
      }
    }
    if (match.blockedByAtomicRequestId) {
      return {
        confirmed: false,
        reason:
          `atomic_prerequisite_pending:${match.blockedByAtomicRequestId}`,
      };
    }
    const op = match.op as unknown as MasterOp;
    const timeoutMs = opts.timeoutMs ?? 8000;
    try {
      const body: SyncPushRequest = {
        sync_protocol: 1,
        client_version:
          (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown',
        client_capabilities: [
          'stock_command_v1',
          'stock_command_dead_letter_v1',
          'sync_poison_isolation_v1',
        ],
        events: [],
        master_ops: [op],
        stock_commands_v1: [],
      };
      const json = await Promise.race([
        this.fetchSync(
          'sync-push',
          { method: 'POST', body: JSON.stringify(body) },
          (j) => SyncPushResponseSchema.parse(j),
        ),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('sync_timeout')), timeoutMs),
        ),
      ]);
      const accepted = json.accepted_masters.includes(id);
      const conflict = json.conflicts.find((c) => c.table === table && c.id === id);
      if (accepted || conflict) {
        // A conflict means the server already holds an authoritative copy (rare
        // for an insert); adopt it so the local mirror matches, then drop the
        // queued op either way — re-pushing it would be pure redundancy.
        if (conflict) {
          try {
            const db = await openLocalDb(null);
            await this.adoptServerMaster(db, conflict.table, conflict.id, conflict.server_value);
          } catch (err) {
            dbg.warn('mobile-sync', 'confirm_master_adopt_failed', {
              table, id, error: toUserMessage(err),
            });
            // Senza adozione non abbiamo una copia locale autorevole da usare
            // né possiamo perdere la retry queue. Fallimento esplicito per
            // qualsiasi chiamante, incluso il flusso QR destinazione.
            return {
              confirmed: false,
              reason: 'master_conflict_adopt_failed',
            };
          }
        }
        await deletePendingOp(match.rowId);
        await flushLocalDb();
        this.setState({ lastPushAt: new Date().toISOString() });
        if (conflict && opts.acceptConflict === false) {
          // Un tick concorrente può avere già applicato ESATTAMENTE questa op:
          // il push mirato usa allora la stessa base_version e riceve un
          // conflitto OCC, pur trovando sul server il payload atteso e la sua
          // versione ottimistica. È sicuro confermarlo solo dopo il confronto
          // strutturale completo dell'expectedPatch; qualsiasi differenza resta
          // un conflitto reale e continua a fallire chiuso.
          if (matchingMasterConflictConfirmsExpectedPatch(
            conflict.server_value,
            opts.expectedPatch,
          )) {
            dbg.info('mobile-sync', 'confirm_master_ok', {
              table,
              id,
              via: 'matching_concurrent_conflict',
            });
            return { confirmed: true };
          }
          dbg.warn('mobile-sync', 'confirm_master_conflict_rejected', {
            table,
            id,
          });
          return { confirmed: false, reason: 'master_version_conflict' };
        }
        dbg.info('mobile-sync', 'confirm_master_ok', {
          table,
          id,
          via: conflict ? 'conflict' : 'accepted',
        });
        return { confirmed: true };
      }
      const rejected = json.rejected_masters.find((r) => r.id === id);
      const reason = rejected?.reason ?? 'not_acknowledged';
      dbg.warn('mobile-sync', 'confirm_master_not_accepted', { table, id, reason });
      return { confirmed: false, reason };
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'sync_failed';
      dbg.warn('mobile-sync', 'confirm_master_failed', { table, id, reason });
      return { confirmed: false, reason };
    }
  }

  /**
   * Adopt the server's authoritative copy of a master row into masters_local,
   * completing an `lww_server` conflict resolution. Mirrors the master upsert in
   * `pull()` so the local mirror matches what a fresh pull would write.
   */
  private async adoptServerMaster(
    db: Awaited<ReturnType<typeof openLocalDb>>,
    table: string,
    id: string,
    serverValue: Record<string, unknown>,
  ): Promise<void> {
    const version = Number((serverValue as { version?: number }).version ?? 0);
    const tenantId = (serverValue as { tenant_id?: string | null }).tenant_id ?? null;
    const archived = (serverValue as { archived_at?: unknown }).archived_at ? 1 : 0;
    await db.run(
      `INSERT INTO masters_local(table_name,id,tenant_id,version,row,archived,updated_at)
       VALUES(?,?,?,?,?,?,datetime('now'))
       ON CONFLICT(table_name,id) DO UPDATE SET
         tenant_id = excluded.tenant_id,
         version = excluded.version,
         row = excluded.row,
         archived = excluded.archived,
         updated_at = excluded.updated_at`,
      [table, id, tenantId, version, JSON.stringify(serverValue), archived],
    );
  }

  /**
   * Reset del cursore quando cambia l'IDENTITÀ loggata su questo device.
   * Il cursore per-tenant (`last_rev_by_tenant`) è persistito nel device e NON
   * è scoped all'utente: se si fa login con un altro account sullo stesso
   * telefono, il nuovo utente eredita il cursore del precedente e `sync-pull`
   * (che filtra `server_rev > cursore`) salta PER SEMPRE gli eventi sotto quel
   * valore → storico "tagliato" (es. consulente che sincronizza un tenant a
   * rev alto, poi login come cliente → mancano i lotti vecchi). Il reset del
   * server scatta solo se il cursore è OLTRE la testa, non a metà storia, quindi
   * va gestito qui lato client. Wipe della sola cache server (events_local); la
   * coda `pending_ops` non viene toccata. Seamless all'upgrade: se non c'è mai
   * stato un `synced_user_id`, lo registra senza resettare (il cursore esistente
   * è assunto del current user); resetta solo a un CAMBIO effettivo di identità.
   */
  private async ensureIdentity(): Promise<boolean> {
    let uid: string | null = null;
    try {
      const { data } = await getSupabase().auth.getSession();
      uid = data.session?.user?.id ?? null;
    } catch {
      return false;
    }
    if (!uid) return false;
    const prev = await getMeta('synced_user_id');
    if (prev === uid) return false;
    const identityChanged = !!prev;
    if (identityChanged) {
      dbg.warn('mobile-sync', 'identity_change_reset', { prev, next: uid });
      // Hard privacy boundary: unlike event JSON, photo bytes on PWA are not
      // encrypted at rest. A new account must never see or upload the previous
      // account's pending evidence.
      const photosDeleted = await purgeProductionLotPhotoOutbox();
      const operationalEvidenceDeleted = await purgeOperationalEvidenceOutsideTenants([]);
      dbg.info('mobile-sync', 'identity_change_photo_outbox_purged', {
        deleted: photosDeleted,
        operational_evidence_deleted: operationalEvidenceDeleted,
      });
      await resetPullCursors();
      try {
        const db = await openLocalDb(null);
        await db.run('DELETE FROM events_local');
        await flushLocalDb();
      } catch (err) {
        dbg.warn('mobile-sync', 'identity_reset_wipe_failed', { error: toUserMessage(err) });
      }
    }
    await setMeta('synced_user_id', uid);
    return identityChanged;
  }

  async pull(): Promise<SyncPullResponse | null> {
    this.setState({ status: 'pulling' });
    const identityChanged = await this.ensureIdentity();
    const serverIdRecovery = await recoverLegacyEventServerIds();
    if (serverIdRecovery.action === 'start_replay') {
      dbg.warn('mobile-sync', 'legacy_server_id_backfill_reset', {
        confirmed_rows: serverIdRecovery.missingConfirmedRows,
      });
    }
    // T-182: cursore PER-TENANT. `server_rev` è una sequenza per-tenant: un
    // cursore singolo globale saltava per sempre gli eventi dei tenant a rev
    // basso (consulente/owner multi-tenant → lotti/eventi mancanti). Mappa
    // {tenant_id: rev} persistita in meta.
    let cursorByTenant: Record<string, number> = {};
    try {
      const parsed = JSON.parse((await getMeta('last_rev_by_tenant')) ?? '{}') as unknown;
      if (parsed && typeof parsed === 'object') cursorByTenant = parsed as Record<string, number>;
    } catch {
      cursorByTenant = {};
    }
    const initialCursor = initialPullCursor(
      cursorByTenant,
      await getMeta('last_rev'),
    );
    const db = await openLocalDb(null);
    let totalEvents = 0;
    let totalMasters = 0;
    let totalMasterFailures = 0;
    let totalServerIdFallbacks = 0;

    const result = await drainPullPages<SyncPullResponse>({
      initialCursor,
      fetchPage: async (cursor, pageIndex) => {
        const json = await this.fetchSync(
          'sync-pull',
          {
            method: 'POST',
            body: JSON.stringify({
              since_rev: cursor.legacy,
              since_rev_by_tenant: cursor.byTenant,
              limit: this.batchSize,
            }),
          },
          (j) => SyncPullResponseSchema.parse(j),
        );

        // Diagnostica: cosa è arrivato nel masters_changed?
        const mastersByTable: Record<string, number> = {};
        for (const m of json.masters_changed) {
          mastersByTable[m.table] = (mastersByTable[m.table] ?? 0) + 1;
        }
        dbg.info('mobile-sync', 'pull_masters_received', {
          page: pageIndex + 1,
          has_more: json.has_more,
          total: json.masters_changed.length,
          events_total: json.events.length,
          by_table: mastersByTable,
          max_rev: json.max_rev,
          since_rev: cursor.legacy,
          // T-128: visibilità lato client dell'auto-recovery server-side.
          stale_cursor: json.stale_cursor,
          auto_reset: json.auto_reset,
          user_head_rev: json.user_head_rev,
        });
        if (json.stale_cursor) {
          // Il server ha rilevato un cursore stantio (events_local del client
          // riferiva un rev > vero head dei tenant attuali) e ha ripullato da 0.
          // Logghiamo a warn per metriche: se diventa ricorrente, bug a monte.
          dbg.warn('mobile-sync', 'stale_cursor_auto_reset', {
            page: pageIndex + 1,
            prev_since_rev: cursor.legacy,
            new_max_rev: json.max_rev,
            user_head_rev: json.user_head_rev,
          });
        }
        return json;
      },
      applyPage: async (json, pageIndex) => {
        // Upsert masters.
        let mastersWritten = 0;
        let mastersFailed = 0;
        const changedMasters = await changedPulledMasters(json.masters_changed);
        for (const m of changedMasters) {
          try {
            await applyPulledMasterToStockCommandCache(m);
            mastersWritten++;
          } catch (err) {
            mastersFailed++;
            dbg.error('mobile-sync', 'master_insert_failed', {
              page: pageIndex + 1,
              table: m.table,
              id: m.id,
              error: toUserMessage(err),
              reason: atomicRpcFailureReason(err),
              row_preview: JSON.stringify(m.row).slice(0, 200),
            });
          }
        }
        if (mastersFailed > 0 || json.masters_changed.length > 0) {
          dbg.info('mobile-sync', 'pull_masters_written', {
            page: pageIndex + 1,
            attempted: json.masters_changed.length,
            unchanged: json.masters_changed.length - changedMasters.length,
            ok: mastersWritten,
            failed: mastersFailed,
          });
        }
        totalMasters += mastersWritten;
        totalMasterFailures += mastersFailed;

        // Cache events. PK locale = client_uuid (NON l'id server) così la riga
        // ottimistica scritta da queueEvent viene rimpiazzata dalla copia
        // confermata dal server. L'id server non lo usiamo lato client.
        let eventsServerIdFallbacks = 0;
        for (const raw of json.events) {
          const ev = raw as Record<string, unknown>;
          const key = confirmedEventLocalIdentity(ev);
          // T-312/#4: per il pull (potenzialmente molti eventi in un full-resync)
          // logghiamo un SOMMARIO per batch + un warn per ogni fallback su server_id
          // (raro e pericoloso, la classe-bug T-311) → niente flood, segnale alto.
          if (key.source === 'server_id') {
            eventsServerIdFallbacks++;
            dbg.warn('events_local', 'write_keyed_on_server_id', {
              writer: 'pull',
              page: pageIndex + 1,
              id: key.id,
              kind: ev.kind as string,
              key_source: key.source,
            });
          }
          await db.run(
            `INSERT OR REPLACE INTO events_local(
               id,server_id,tenant_id,kind,business_id,client_ts,server_ts,rev,
               payload,self_hash,prev_hash
             ) VALUES(?,?,?,?,?,?,?,?,?,?,?)`,
            [
              key.id,
              key.serverId,
              ev.tenant_id as string,
              ev.kind as string,
              (ev.business_id as string | null) ?? null,
              ev.client_ts as string,
              (ev.server_ts as string | null) ?? null,
              (ev.server_rev as number | null) ?? null,
              JSON.stringify(ev.payload ?? {}),
              (ev.self_hash as string | null) ?? null,
              (ev.prev_hash as string | null) ?? null,
            ],
          );
        }
        if (json.events.length > 0) {
          dbg.info('events_local', 'pull_applied', {
            writer: 'pull',
            page: pageIndex + 1,
            count: json.events.length,
            server_id_fallbacks: eventsServerIdFallbacks,
          });
        }
        totalEvents += json.events.length;
        totalServerIdFallbacks += eventsServerIdFallbacks;
        // Keep the page eligible for replay when SQLite did not persist all masters.
        // Push still runs on each tick; successful writes above are idempotent.
        if (mastersFailed > 0) throw new Error(`sync_pull_master_write_failed:${mastersFailed}`);
      },
      // Checkpoint durevole DOPO ogni pagina applicata. Se il safety limit
      // interrompe un backlog enorme, il tick successivo riparte dall'ultima
      // pagina completa; un crash prima del checkpoint riapplica gli upsert
      // idempotenti senza perdere eventi.
      commitCursor: async (cursor) => {
        if (cursor.sawPerTenantResponse) {
          await setMeta('last_rev_by_tenant', JSON.stringify(cursor.byTenant));
        }
        await setMeta('last_rev', String(cursor.legacy));
      },
    });
    const replayedTenantIds = Object.keys(result.finalPage.max_rev_by_tenant ?? {});
    const serverIdBackfillFinalized = await finalizeLegacyEventServerIdRecovery(replayedTenantIds);
    // Finalization can delete this tenant's stale rows while the global marker
    // stays pending for another tenant. Refresh readers after either outcome.
    const serverIdBackfillMayHaveChangedData = replayedTenantIds.length > 0
      && (serverIdRecovery.action === 'start_replay' || serverIdRecovery.action === 'resume_replay');

    dbg.info('mobile-sync', 'pull_drained', {
      pages: result.pages,
      events_total: totalEvents,
      masters_written: totalMasters,
      masters_failed: totalMasterFailures,
      server_id_fallbacks: totalServerIdFallbacks,
      legacy_server_id_backfill_finalized: serverIdBackfillFinalized,
      max_rev: result.cursor.legacy,
    });

    this.setState({
      lastPullAt: new Date().toISOString(),
      lastPullChangedData: identityChanged || totalMasters > 0 || totalEvents > 0 || serverIdBackfillMayHaveChangedData,
    });
    return result.finalPage;
  }
}

let engine: SyncEngine | null = null;

export function getSyncEngine(opts?: SyncEngineOptions): SyncEngine {
  if (!engine) engine = new SyncEngine(opts);
  return engine;
}

export type { PendingOp };
export type {
  FinalizeProductionLabelPrintIntentInput,
  PendingProductionLabelPrintOutcome,
  ProductionLabelPrintReconciliation,
  ProductionLabelPrintIntent,
  ProductionLabelPrintIntentState,
} from './productionLabelPrintIntentOutbox.js';
